import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X, Save, Upload, Activity, FileText, Stethoscope, Clock, Heart, Users,
  ChevronUp, ChevronDown, Plus, Check, ChevronRight, AlertCircle, HelpCircle, Trash2
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faEye, faEyeSlash, faTimes, faList } from '@fortawesome/free-solid-svg-icons';
import EventLogSection from '../../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../../hooks/useEventLogModalScrollLock';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { resolvePracticeLogoUrl } from '../../../../utils/assetUrls';
import { isDateOnlyAfterToday, getMaxDateOfBirthForInput, DATE_OF_BIRTH_FUTURE_ERROR } from '../../../../utils/dateOnlyUtils';
import { toast } from 'react-toastify';
import {
  fetchSystemCaregivers,
  fetchPracticeProviders,
  fetchPracticeCaregivers,
  fetchPractice
} from '../../../../store/slices/practiceSlice';
import { createPatient, updatePatient } from '../../../../store/slices/patientSlice';
import inventoryService from '../../../../services/inventoryService';
import careProgramService from '../../../../services/careProgramService';
import patientService from '../../../../services/patientService';
import BpEnrollmentSettingsModal, { monitoringVitalKind } from './BpEnrollmentSettingsModal';
import { applyEnrollmentScheduleChoice } from '../../../../utils/enrollmentScheduleTargets';
import { readVitalScheduleTargetMode, writeVitalScheduleTargetMode } from '../../../../utils/patientScheduleTargetPreference';
import {
  getLucideProgramIcon,
  buildProgramTabsFromEnrolled,
  normalizeProgramKey,
  filterActiveCarePrograms,
  getProgramsForPractice,
  sortProgramKeysForDisplay
} from '../../../../utils/careProgramUtils';
import { TIME_ZONES, US_STATES, normalizeTimeZone, getStateNameFromPatientRecord, getStateIdFromName, EMERGENCY_CONTACT_RELATIONS } from '../../../../utils/constants';
import { validatePassword, applyPasswordServerFieldError } from '../../../../utils/passwordValidation';
import './AddPatientModal.css';
import useFocusTrap from '../../../../hooks/useFocusTrap';
import { createModalEscapeKeyDownHandler } from '../../../../utils/modalEscape';
import { reportValidationErrors, validateRequiredEmail, validateRequiredPhone, preserveAvailabilityErrors, hasAvailabilityConflict, updateFieldErrorOnChange, clearFieldError } from '../../../../utils/formValidation';
import {
  sanitizeInsuranceField,
  getInsuranceFieldError,
  validateInsuranceData,
  insuranceErrorKey,
  isInsuranceErrorKey,
} from '../../../../utils/insuranceFieldValidation';
import { validateImageUploadFile } from '../../../../utils/imageUploadUtils';
import { buildPatientProfileImageUrl } from '../../../../utils/patientProfileImage.js';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import AppDateInput from '../../../../components/Common/AppDateInput/AppDateInput';
import {
  METER_EXTENSION_OPTIONS,
  normalizeDeviceSerialInput,
  validateMeterMappingList,
  isCompleteMeterFormRow,
  formatMeterProductOptionLabel,
} from '../../../../utils/meterMappingUtils';
import { sanitizeEmailInput } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import FormFieldError from '../../../../components/Common/FormFieldError/FormFieldError';
import SignaturePad from '../../../../components/Common/SignaturePad/SignaturePad';
import { getConsentSignatureValidationError } from '../../../../utils/consentSignatureValidation';
import PatientDocumentsSection from '../../../../components/PatientDocuments/PatientDocumentsSection';
import PatientChronicConditionsSection from '../../../../components/PatientChronicConditions/PatientChronicConditionsSection';
import {
  normalizePatientChronicConditions,
  serializePatientChronicConditions,
} from '../../../../utils/patientChronicConditionUtils';
import { practiceHasProgram } from '../../../../utils/pcmCcmEnrollment';
import { patientStatusFormValue, normalizePatientStatusId } from '../../../../utils/patientStatusUtils';
import PatientStatusDeviceRemovalModal from '../../../../components/Common/PatientStatusDeviceRemovalModal/PatientStatusDeviceRemovalModal';
import {
  PatientAscStyleSinglePicker,
  PatientAscStyleMultiPicker,
  buildCaregiverOptionsWithStubs,
  expandCaregiverIdsFromApi,
  resolvePracticeCaregiverIdsFromPatient,
  toNumericCaregiverIds,
  isStaffActive
} from '../shared/PatientCaregiverPickers';
import { isEvitalsMonitoringPractice } from '../../../../utils/practiceMonitoringUtils';
import lookupService from '../../../../services/lookupService';
import IcdCodeSearchField from '../../../../components/Common/IcdCodeSearchField/IcdCodeSearchField';
import {
  buildMonitoringTypesPayload,
  emptyMonitoringIcdValues,
  getIcdOptionsForMonitoringLabel,
  groupIcdCodesByMeasureType,
  parsePatientMonitoringSelection,
} from '../../../../utils/patientMonitoringIcdUtils';

function emptyMeterMappingRow() {
  return { modelNumber: '', extension: '', serialNumber: '', gatewaySerialNumber: '' };
}

function emptyInsuranceTier() {
  return { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' };
}

const PROVIDER_TITLE_BY_ID = { 1: 'Dr.', 2: 'Mr.', 3: 'Ms.', 4: 'Mrs.' };

function formatConsentProviderName(provider) {
  if (!provider) return '';
  const titleRaw = provider.title;
  const titleFromId = PROVIDER_TITLE_BY_ID[Number(provider.title_id ?? provider.title)] || '';
  const title =
    typeof titleRaw === 'string' && /[A-Za-z]/.test(titleRaw) && titleRaw.trim().length < 12
      ? titleRaw.trim()
      : titleFromId;
  const name = [
    provider.first_name || provider.firstName,
    provider.last_name || provider.lastName
  ].filter(Boolean).join(' ').trim()
    || String(provider.name || provider.full_name || '').trim();
  if (!name) return '';
  if (!title) return name;
  const titleToken = title.replace(/\./g, '').toLowerCase();
  if (name.toLowerCase().startsWith(titleToken)) return name;
  return `${title} ${name}`.replace(/\s+/g, ' ').trim();
}

function formatConsentParty(providerName, practiceName) {
  if (providerName && practiceName) return `${providerName} with ${practiceName}`;
  return providerName || practiceName || 'the practice';
}

/** Browser cache for consent form (signature dates, etc.) when the patient API does not return them. */
const PATIENT_CONSENT_STORAGE_PREFIX = 'rpm_patient_consent_v1';

function patientConsentStorageKey(practiceId, patientTableId) {
  if (practiceId == null || patientTableId == null) return null;
  return `${PATIENT_CONSENT_STORAGE_PREFIX}:${practiceId}:${patientTableId}`;
}

function savePatientConsentDraft(practiceId, patientTableId, consent) {
  const key = patientConsentStorageKey(practiceId, patientTableId);
  if (!key || !consent || typeof consent !== 'object') return;
  try {
    localStorage.setItem(key, JSON.stringify(consent));
  } catch (e) {
    console.warn('[AddPatientModal] Could not cache consent form in localStorage:', e?.message || e);
  }
}

function readPatientConsentDraft(practiceId, patientTableId) {
  const key = patientConsentStorageKey(practiceId, patientTableId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw || !String(raw).trim().startsWith('{')) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const CONSENT_PDF_PAGE_MARGIN_MM = 10;
const CONSENT_PDF_SAFETY_MM = 3;

function waitForElementImages(root) {
  const images = Array.from(root.querySelectorAll('img'));
  if (images.length === 0) return Promise.resolve();
  return Promise.all(
    images.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    })
  );
}

function rowIsMostlyWhite(data, rowOffset, width, sampleStep = 4) {
  let dark = 0;
  let sampled = 0;
  for (let x = 0; x < width; x += sampleStep) {
    const i = rowOffset + x * 4;
    sampled += 1;
    if (data[i] < 248 || data[i + 1] < 248 || data[i + 2] < 248) dark += 1;
  }
  return dark / Math.max(sampled, 1) < 0.02;
}

/** Split on a blank gap so labels/fields are not cut in half across pages. */
function findSafeSplitY(canvas, startY, idealEndY) {
  const available = idealEndY - startY;
  if (available <= 8) return idealEndY;
  const scanDepth = Math.min(160, Math.floor(available * 0.4));
  const scanStart = Math.max(startY, idealEndY - scanDepth);
  const height = idealEndY - scanStart;
  try {
    const ctx = canvas.getContext('2d');
    const { width } = canvas;
    const { data } = ctx.getImageData(0, scanStart, width, height);
    const rowSize = width * 4;
    for (let row = height - 1; row >= 0; row -= 1) {
      if (!rowIsMostlyWhite(data, row * rowSize, width)) continue;
      let bandTop = row;
      while (bandTop > 0 && rowIsMostlyWhite(data, (bandTop - 1) * rowSize, width)) {
        bandTop -= 1;
      }
      const splitRow = bandTop + Math.floor((row - bandTop) / 2);
      return scanStart + splitRow;
    }
  } catch (err) {
    // Canvas may be tainted (cross-origin logo); fall back below.
  }
  return Math.max(startY + Math.floor(available * 0.7), idealEndY - 96);
}

function saveCanvasAsMultipagePdf(canvas, filename) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - CONSENT_PDF_PAGE_MARGIN_MM * 2;
  const contentHeight = pageHeight - CONSENT_PDF_PAGE_MARGIN_MM * 2 - CONSENT_PDF_SAFETY_MM;
  const pxPerMm = canvas.width / contentWidth;
  const pageHeightPx = Math.max(1, Math.floor(contentHeight * pxPerMm));

  let yPx = 0;
  let pageIndex = 0;
  while (yPx < canvas.height) {
    const remaining = canvas.height - yPx;
    const idealEnd = yPx + Math.min(pageHeightPx, remaining);
    const splitY = remaining <= pageHeightPx
      ? canvas.height
      : findSafeSplitY(canvas, yPx, idealEnd);
    const slicePx = Math.max(1, splitY - yPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = Math.ceil(slicePx);
    const ctx = sliceCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(canvas, 0, yPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
    const sliceHeightMm = Math.min(slicePx / pxPerMm, contentHeight);
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(
      sliceCanvas.toDataURL('image/png'),
      'PNG',
      CONSENT_PDF_PAGE_MARGIN_MM,
      CONSENT_PDF_PAGE_MARGIN_MM,
      contentWidth,
      sliceHeightMm,
      undefined,
      'FAST'
    );
    yPx += slicePx;
    pageIndex += 1;
  }
  pdf.save(filename);
}

const PATIENT_CONSENT_PAYLOAD_KEYS = [
  'name', 'dob', 'tel', 'address', 'email',
  'patientSignature', 'patientDate', 'clinicianSignature', 'clinicianDate',
  'caregiverName', 'caregiverSignature', 'caregiverDate'
];

function parsePatientConsentFromPayload(patient) {
  if (!patient || typeof patient !== 'object') return null;
  const raw =
    patient.consent_data ??
    patient.consentData ??
    patient.consent_form_snapshot ??
    patient.consentFormSnapshot;
  let obj = raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s || (!s.startsWith('{') && !s.startsWith('['))) return null;
    try {
      obj = JSON.parse(s);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const hasKnown = PATIENT_CONSENT_PAYLOAD_KEYS.some((k) => Object.prototype.hasOwnProperty.call(obj, k));
  if (!hasKnown) return null;
  return obj;
}

function buildEditModeConsentFormState({ patient, dobValue, homeDigits, formatPhoneNumberFn, fromApi, fromStorage: _fromStorage }) {
  const demographicFallback = {
    name: `${patient.first_name || patient.firstName || ''} ${patient.last_name || patient.lastName || ''}`.trim(),
    dob: dobValue,
    tel: homeDigits ? formatPhoneNumberFn(homeDigits) : '',
    address: [
      patient.address_line_1 || patient.addressLine1 || patient.address?.address_line_1 || '',
      patient.address_line_2 || patient.addressLine2 || '',
      patient.city || '',
      patient.state || '',
      patient.zip_code || patient.zipCode || ''
    ].filter(Boolean).join(', '),
    email: patient.email || ''
  };
  const api = fromApi && typeof fromApi === 'object' && !Array.isArray(fromApi) ? fromApi : {};
  const merged = {
    patientSignature: api.patientSignature || '',
    patientDate: api.patientDate || '',
    clinicianSignature: api.clinicianSignature || '',
    clinicianDate: api.clinicianDate || '',
    caregiverName: api.caregiverName || '',
    caregiverSignature: api.caregiverSignature || '',
    caregiverDate: api.caregiverDate || '',
    ...demographicFallback
  };
  return merged;
}

const PATIENT_DEMOGRAPHIC_ERROR_KEYS = new Set([
  'firstName', 'lastName', 'username', 'dateOfBirth', 'ssn',
  'homePhone', 'cellPhone', 'fax', 'email', 'addressLine1', 'city', 'state', 'zipCode', 'timeZone',
  'provider', 'systemCaregiver', 'practiceCaregiver', 'password', 'confirmPassword', 'profilePicture'
]);

function emptyAddPatientFormState() {
  return {
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    emergencyContactFirstName: '',
    emergencyContactLastName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    emergencyContactAddress: '',
    email: '',
    gender: 'Male',
    dateOfBirth: '',
    twoWayAuth: 'Enabled',
    mobileAppAccess: 'Disabled',
    status: 'Active',
    timeZone: '',
    rpmStartDate: '',
    provider: '',
    username: '',
    password: '',
    confirmPassword: '',
    systemCaregiverIds: [],
    practiceCaregiverIds: [],
    ssn: ''
  };
}

export const AddPatientModal = ({ practiceId, onClose, onSave, patientToEdit = null, defaultProgramTab: _defaultProgramTab = null }) => {
  const dispatch = useDispatch();

  // Select data from Redux practice slice
  const { systemCaregivers = [], practiceProviders = [], practiceCaregivers = [], currentPractice } = useSelector(state => state.practice ?? {});
  const safeSystemCaregivers = Array.isArray(systemCaregivers) ? systemCaregivers : (systemCaregivers?.caregivers || systemCaregivers?.data || []);
  const requiresSystemCaregiver = isEvitalsMonitoringPractice(currentPractice);
  const safePracticeProviders = Array.isArray(practiceProviders) ? practiceProviders : (practiceProviders?.data || []);
  const safePracticeCaregivers = Array.isArray(practiceCaregivers) ? practiceCaregivers : (practiceCaregivers?.data || []);

  const isActive = (item) => isStaffActive(item);
  const activeSystemCaregivers = useMemo(() => safeSystemCaregivers.filter(isActive), [safeSystemCaregivers]);
  const activePracticeProviders = useMemo(() => safePracticeProviders.filter(isActive), [safePracticeProviders]);
  const activePracticeCaregivers = useMemo(() => safePracticeCaregivers.filter(isActive), [safePracticeCaregivers]);

  // Helper: build a display name from first/last/name fields
  const getDisplayName = (item) => {
    if (!item) return '';
    if (item.name) return item.name;
    const first = item.first_name || item.firstName || '';
    const last = item.last_name || item.lastName || '';
    return [first, last].filter(Boolean).join(' ') || item.username || item.email || '';
  };

  const effectivePracticeId = useMemo(() => {
    return (
      practiceId ||
      patientToEdit?.patient?.practice_id ||
      patientToEdit?.practice_id ||
      patientToEdit?.patient?.practiceId ||
      patientToEdit?.practiceId ||
      currentPractice?.id ||
      null
    );
  }, [practiceId, patientToEdit, currentPractice?.id]);

  const editPatientId = useMemo(() => {
    const p = patientToEdit?.patient || patientToEdit;
    return p?.id || null;
  }, [patientToEdit]);

  const [showEventLogsModal, setShowEventLogsModal] = useState(false);
  const [patientLogFilter, setPatientLogFilter] = useState('all');
  const logOverlayRef = useRef(null);
  const logContainerRef = useRef(null);
  useEventLogModalScrollLock(showEventLogsModal, logOverlayRef, logContainerRef);

  const knownCaregiverObjects = useMemo(() => {
    const p = patientToEdit?.patient || patientToEdit;
    if (!p) return [];
    return [
      ...(Array.isArray(p.practice_caregivers) ? p.practice_caregivers : []),
      ...(Array.isArray(p.practiceCaregivers) ? p.practiceCaregivers : []),
      ...(Array.isArray(p.caregivers) ? p.caregivers : []),
      p.practice_caregiver,
      p.practiceCaregiver,
      p.caregiver
    ].filter(Boolean);
  }, [patientToEdit]);

  const knownSystemCaregiverObjects = useMemo(() => {
    const p = patientToEdit?.patient || patientToEdit;
    if (!p) return [];
    return [
      ...(Array.isArray(p.system_caregivers) ? p.system_caregivers : []),
      ...(Array.isArray(p.systemCaregivers) ? p.systemCaregivers : []),
      p.system_caregiver,
      p.systemCaregiver
    ].filter(Boolean);
  }, [patientToEdit]);

  // Component states
  const [formData, setFormData] = useState(() => emptyAddPatientFormState());
  const systemCaregiverPickerOptions = useMemo(
    () => buildCaregiverOptionsWithStubs(activeSystemCaregivers, formData.systemCaregiverIds, knownSystemCaregiverObjects),
    [activeSystemCaregivers, formData.systemCaregiverIds, knownSystemCaregiverObjects]
  );
  const practiceCaregiverPickerOptions = useMemo(
    () => buildCaregiverOptionsWithStubs(activePracticeCaregivers, formData.practiceCaregiverIds, knownCaregiverObjects),
    [activePracticeCaregivers, formData.practiceCaregiverIds, knownCaregiverObjects]
  );
  const [carePrograms, setCarePrograms] = useState([]);
  const [selectedCarePrograms, setSelectedCarePrograms] = useState([]);
  const [selectedMonitoring, setSelectedMonitoring] = useState([]);
  const [monitoringIcdValues, setMonitoringIcdValues] = useState(() => emptyMonitoringIcdValues());
  const [icdCodesByMeasureType, setIcdCodesByMeasureType] = useState({ 1: [], 2: [], 3: [] });
  const [selectedChronicConditions, setSelectedChronicConditions] = useState([]);
  const [ccmRestrictionsConfirmed, setCcmRestrictionsConfirmed] = useState(false);
  const [products, setProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meterMappings, setMeterMappings] = useState([emptyMeterMappingRow()]);
  const [statusDeviceRemovalModal, setStatusDeviceRemovalModal] = useState({ show: false, targetStatus: '' });

  const handleConfirmStatusDeviceRemoval = () => {
    const target = statusDeviceRemovalModal.targetStatus || 'Pending';
    setFormData((prev) => ({ ...prev, status: target }));
    setMeterMappings([emptyMeterMappingRow()]);
    setStatusDeviceRemovalModal({ show: false, targetStatus: '' });
    toast.info(`Assigned devices removed. Status set to ${target}. Click Save Patient to apply.`);
  };

  const handleCancelStatusDeviceRemoval = () => {
    setStatusDeviceRemovalModal({ show: false, targetStatus: '' });
    setFormData((prev) => ({ ...prev, status: 'Active' }));
  };

  const [insuranceData, setInsuranceData] = useState({
    primary: emptyInsuranceTier(),
    secondary: emptyInsuranceTier(),
  });

  const pictureInputRef = useRef(null);
  const documentsSectionRef = useRef(null);
  const consentPdfBusyRef = useRef(false);
  const vitalAutoPromptedRef = useRef(false);
  const vitalConfirmedRef = useRef({ bp: false, bg: false, weight: false });
  const [vitalModal, setVitalModal] = useState(null);
  const [vitalModes, setVitalModes] = useState({ bp: null, bg: null, weight: null });
  const [vitalDrafts, setVitalDrafts] = useState({ bp: null, bg: null, weight: null });
  const [pictureFile, setPictureFile] = useState(null);
  const [pendingDocumentCount, setPendingDocumentCount] = useState(0);
  const [existingProfilePic, setExistingProfilePic] = useState('');
  const [pictureRemoved, setPictureRemoved] = useState(false);

  const existingProfilePicUrl = useMemo(
    () => buildPatientProfileImageUrl(existingProfilePic),
    [existingProfilePic]
  );

  const handlePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const pictureError = validateImageUploadFile(file, { label: 'Profile picture' });
    if (pictureError) {
      setErrors((prev) => ({ ...prev, profilePicture: pictureError }));
      e.target.value = '';
      return;
    }
    clearFieldError(setErrors, 'profilePicture');
    setPictureFile(file);
    setExistingProfilePic('');
    setPictureRemoved(false);
  };

  const handleRemovePicture = () => {
    setPictureFile(null);
    setExistingProfilePic('');
    setPictureRemoved(true);
    clearFieldError(setErrors, 'profilePicture');
    if (pictureInputRef.current) pictureInputRef.current.value = '';
  };

  // Dynamic Stepper State
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '', ssn: '' });
  const excludeUserId = useMemo(() => {
    if (!patientToEdit) return null;
    const p = patientToEdit.patient || patientToEdit;
    return p.user_id ?? p.userId ?? null;
  }, [patientToEdit]);
  const excludePatientId = useMemo(() => {
    if (!patientToEdit) return null;
    const p = patientToEdit.patient || patientToEdit;
    return p.id ?? p.patient_id ?? p.patient_table_id ?? null;
  }, [patientToEdit]);

  const editingPatientId = excludePatientId;
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    scheduleSsnCheck,
    handleUsernamePaste,
    handleUsernameBlur: scheduleUsernameBlurCheck,
    handleEmailPaste,
    handleEmailBlur: scheduleEmailBlurCheck,
    ensureUsernameAvailable,
    ensureEmailAvailable,
    ensureSsnAvailable,
    checking,
  } = useUsernameEmailAvailability({
    excludeUserId,
    excludePatientId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
    originalSsn: loadedCredentials.ssn,
  });
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [initialEditSnapshot, setInitialEditSnapshot] = useState(null);

  // CCM specific consent state matching Design 3/C
  const [ccmConsentType, setCcmConsentType] = useState('verbal');
  const [ccmVerbalAnswers, setCcmVerbalAnswers] = useState([false, false, false]);
  const [ccmVerbalConsentDate, setCcmVerbalConsentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [placeholderProgramStartDate, setPlaceholderProgramStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [ccmConsentSignature, setCcmConsentSignature] = useState(null);
  const [ccmConsentSignatureSource, setCcmConsentSignatureSource] = useState('draw');

  // RPM Patient Consent Form states
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentPdfDownloading, setConsentPdfDownloading] = useState(false);
  const [consentLogoFailed, setConsentLogoFailed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentForm, setConsentForm] = useState({
    name: '',
    dob: '',
    tel: '',
    address: '',
    email: '',
    patientSignature: '',
    patientDate: '',
    clinicianSignature: '',
    clinicianDate: '',
    caregiverName: '',
    caregiverSignature: '',
    caregiverDate: '',
  });

  const practiceLogoUrl = useMemo(() => {
    return resolvePracticeLogoUrl(currentPractice?.practice_logo || currentPractice?.logo);
  }, [currentPractice]);

  const consentAssignedProviderName = useMemo(() => {
    const match = (safePracticeProviders || []).find((p) => String(p?.id) === String(formData.provider));
    const fromList = formatConsentProviderName(match);
    if (fromList) return fromList;
    const p = patientToEdit?.patient || patientToEdit;
    return String(p?.provider_name || p?.providerName || '').trim();
  }, [formData.provider, safePracticeProviders, patientToEdit]);

  const consentPracticeName = useMemo(() => {
    const p = patientToEdit?.patient || patientToEdit;
    return String(
      currentPractice?.practice_name
      || currentPractice?.name
      || p?.practice_name
      || p?.practiceName
      || ''
    ).trim();
  }, [currentPractice, patientToEdit]);

  const consentIntroParty = useMemo(
    () => formatConsentParty(consentAssignedProviderName, consentPracticeName),
    [consentAssignedProviderName, consentPracticeName]
  );

  const practiceCreatedAtStr = useMemo(() => {
    const raw = currentPractice?.created_at || currentPractice?.createdAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, [currentPractice?.created_at, currentPractice?.createdAt]);

  const CONSENT_DATE_FIELDS = ['patientDate', 'clinicianDate', 'caregiverDate'];
  const CONSENT_DATE_LABELS = {
    patientDate: 'Patient signature date',
    clinicianDate: 'Clinician signature date',
    caregiverDate: 'Caregiver/relative signature date'
  };

  const isConsentDateBeforePracticeCreation = (value) => {
    if (!value || !practiceCreatedAtStr) return false;
    return value < practiceCreatedAtStr;
  };

  const handleConsentChange = (e) => {
    const { name, value } = e.target;

    if (name === 'dob' && value && isDateOnlyAfterToday(value)) {
      toast.warning(DATE_OF_BIRTH_FUTURE_ERROR);
      return;
    }

    if (CONSENT_DATE_FIELDS.includes(name) && value && isConsentDateBeforePracticeCreation(value)) {
      toast.warning(
        `${CONSENT_DATE_LABELS[name]} cannot be earlier than the practice creation date (${practiceCreatedAtStr}).`
      );
      return;
    }

    setConsentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAgreeToConsent = () => {
    if (!patientToEdit) {
      if (!consentForm.caregiverDate) {
        toast.error(`${CONSENT_DATE_LABELS.caregiverDate} is required.`);
        return;
      }
      if (isConsentDateBeforePracticeCreation(consentForm.caregiverDate)) {
        toast.error(
          `${CONSENT_DATE_LABELS.caregiverDate} cannot be earlier than the practice creation date (${practiceCreatedAtStr}).`
        );
        return;
      }

      for (const f of ['patientDate', 'clinicianDate']) {
        const dateVal = consentForm[f];
        if (dateVal && isConsentDateBeforePracticeCreation(dateVal)) {
          toast.error(
            `${CONSENT_DATE_LABELS[f]} cannot be earlier than the practice creation date (${practiceCreatedAtStr}).`
          );
          return;
        }
      }
    }

    setTermsAccepted(true);
    setShowConsentModal(false);
    setErrors(prev => {
      if (!prev.terms) return prev;
      const next = { ...prev };
      delete next.terms;
      return next;
    });
  };

  const handleTermsClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const addressParts = [
      formData.addressLine1,
      formData.addressLine2,
      formData.city,
      formData.state,
      formData.zipCode
    ].filter(Boolean).join(', ');

    setConsentForm(prev => ({
      ...prev,
      name: `${formData.firstName} ${formData.lastName}`.trim() || prev.name,
      dob: formData.dateOfBirth || prev.dob,
      tel: formData.cellPhone || formData.homePhone || prev.tel,
      address: addressParts || prev.address,
      email: formData.email || prev.email,
    }));
    setConsentLogoFailed(false);
    setShowConsentModal(true);
  };

  const handleDownloadPDF = async () => {
    const source = document.getElementById('printable-consent-form');
    if (!source || consentPdfBusyRef.current) return;

    consentPdfBusyRef.current = true;
    setConsentPdfDownloading(true);

    const filename = `Consent_Form_${(consentForm.name || 'Patient').replace(/\s+/g, '_')}.pdf`;
    let host = null;
    try {
      const clone = source.cloneNode(true);
      clone.removeAttribute('id');
      clone.querySelectorAll('[data-html2canvas-ignore]').forEach((el) => el.remove());

      const sourceInputs = source.querySelectorAll('input.add-patient-consent-input-line');
      clone.querySelectorAll('input.add-patient-consent-input-line').forEach((input, index) => {
        const live = sourceInputs[index];
        if (!live) return;
        input.setAttribute('value', live.value);
        input.value = live.value;
      });

      Object.assign(clone.style, {
        position: 'relative',
        width: '794px',
        maxWidth: '794px',
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
        display: 'block',
        flex: 'none',
        minHeight: '0',
        boxShadow: 'none',
        borderRadius: '0',
        margin: '0',
      });

      const cloneScroll = clone.querySelector('.add-patient-consent-scroll-body');
      if (cloneScroll) {
        Object.assign(cloneScroll.style, {
          height: 'auto',
          maxHeight: 'none',
          overflow: 'visible',
          flex: 'none',
          minHeight: '0',
          paddingBottom: '72px',
        });
      }

      host = document.createElement('div');
      host.className = 'consent-pdf-capture-host';
      host.setAttribute('aria-hidden', 'true');
      host.appendChild(clone);
      document.body.appendChild(host);
      await waitForElementImages(clone);
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      const captureWidth = Math.max(clone.scrollWidth, clone.offsetWidth, 794);
      const captureHeight = Math.max(clone.scrollHeight, clone.offsetHeight) + 24;
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: captureWidth,
        height: captureHeight,
        windowWidth: captureWidth,
        windowHeight: captureHeight,
        scrollX: 0,
        scrollY: 0,
      });
      saveCanvasAsMultipagePdf(canvas, filename);
    } catch (err) {
      console.error('[AddPatientModal] Consent PDF download failed:', err);
      toast.error('Could not download the consent form. Please try again.');
    } finally {
      host?.remove();
      consentPdfBusyRef.current = false;
      setConsentPdfDownloading(false);
    }
  };

  const isChanged = useMemo(() => {
    if (!patientToEdit || !initialEditSnapshot) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    if (pictureFile) return true;
    if (pendingDocumentCount > 0) return true;
    const current = JSON.stringify({
      formData: { ...formData, password: '', confirmPassword: '' },
      selectedCarePrograms,
      selectedMonitoring,
      monitoringIcdValues,
      selectedChronicConditions,
      ccmRestrictionsConfirmed,
      meterMappings,
      insuranceData,
      ccmConsentType,
      ccmConsentSignature,
      ccmVerbalAnswers,
    });
    return current !== initialEditSnapshot;
  }, [
    patientToEdit,
    initialEditSnapshot,
    formData,
    pictureFile,
    pendingDocumentCount,
    selectedCarePrograms,
    selectedMonitoring,
    monitoringIcdValues,
    selectedChronicConditions,
    ccmRestrictionsConfirmed,
    meterMappings,
    insuranceData,
    ccmConsentType,
    ccmConsentSignature,
    ccmVerbalAnswers,
  ]);

  // For Edit Mode: Keep track of originally enrolled programs to show confirmation on removal
  const [originalEnrolledPrograms, setOriginalEnrolledPrograms] = useState([]);
  const [programConfirmModal, setProgramConfirmModal] = useState({ isOpen: false, programKey: null, programObj: null });

  const availableMonitoringTypes = useMemo(() => {
    const rpmProgram = currentPractice?.enrolled_programs?.find(p => p.short_name?.toLowerCase() === 'rpm' || p.program?.toLowerCase() === 'rpm');
    let params = rpmProgram?.parameters;
    if (typeof params === 'string') {
      try {
        params = JSON.parse(params);
      } catch {
        params = null;
      }
    }
    const isOn = (value) => value === true || value === 1 || value === '1' || value === 'true';
    const source = params && typeof params === 'object' ? params : null;
    const types = [];
    if (source) {
      if (isOn(source.blood_pressure) || isOn(source.bloodPressure)) types.push('Blood Pressure');
      if (isOn(source.blood_glucose) || isOn(source.bloodGlucose)) types.push('Blood Glucose');
      if (isOn(source.weight)) types.push('Weight');
    }
    // Fallback if none are configured
    if (types.length === 0) types.push('Blood Pressure', 'Blood Glucose', 'Weight');
    return types;
  }, [currentPractice]);

  // New enrollments: auto-select when practice offers exactly one monitoring type.
  // That type waits for a required practice-or-custom choice.
  useEffect(() => {
    if (patientToEdit) return;
    if (availableMonitoringTypes.length !== 1) return;
    const type = availableMonitoringTypes[0];
    const kind = monitoringVitalKind(type);
    if (!kind) return;
    if (vitalAutoPromptedRef.current) return;
    vitalAutoPromptedRef.current = true;
    setVitalModal(kind);
  }, [availableMonitoringTypes, patientToEdit]);

  // ── Populate form when editing an existing patient ──────────────────────────
  useEffect(() => {
    if (!patientToEdit) {
      setInitialEditSnapshot(null);
      setLoadedCredentials({ username: '', email: '', ssn: '' });
      setPictureFile(null);
      setExistingProfilePic('');
      setPictureRemoved(false);
      setTermsAccepted(false);
      setConsentForm({
        name: '',
        dob: '',
        tel: '',
        address: '',
        email: '',
        patientSignature: '',
        patientDate: '',
        clinicianSignature: '',
        clinicianDate: '',
        caregiverName: '',
        caregiverSignature: '',
        caregiverDate: '',
      });
      setCcmConsentType('verbal');
      setCcmVerbalAnswers([false, false, false]);
      setCcmConsentSignature(null);
      setCcmConsentSignatureSource('draw');
      setVitalModes({ bp: null, bg: null, weight: null });
      setVitalDrafts({ bp: null, bg: null, weight: null });
      vitalConfirmedRef.current = { bp: false, bg: false, weight: false };
      return;
    }

    // API may nest the real record under .patient or return it flat
    const p = patientToEdit.patient || patientToEdit;

    const safeDateStr = (val) => {
      if (!val) return '';
      try { return new Date(val).toISOString().split('T')[0]; } catch { return ''; }
    };

    const stateName = getStateNameFromPatientRecord(p);

    const mappedFormData = {
      ...emptyAddPatientFormState(),
      title: p.title || (p.title_id ? { 1: 'Dr.', 2: 'Mr.', 3: 'Ms.', 4: 'Mrs.' }[p.title_id] : '') || '',
      firstName: p.first_name || p.firstName || '',
      middleInitial: (p.middle_name ? String(p.middle_name).trim() : (p.middle_initial || p.middleInitial || '')),
      lastName: p.last_name || p.lastName || '',
      addressLine1: p.address_line_1 || p.addressLine1 || p.address || '',
      addressLine2: p.address_line_2 || p.addressLine2 || '',
      city: p.city || '',
      state: stateName,
      stateId: getStateIdFromName(stateName),
      zipCode: p.zip_code || p.zipCode || p.zip || '',
      ssn: p.ssn || '',
      homePhone: formatUSPhoneNumber(p.cell_phone || p.cellPhone || ''),
      cellPhone: formatUSPhoneNumber(p.home_phone || p.homePhone || p.phone || ''),
      fax: formatUSPhoneNumber(p.fax || ''),
      emergencyContactFirstName: p.emergency_contact_first_name || p.emergencyContactFirstName || '',
      emergencyContactLastName: p.emergency_contact_last_name || p.emergencyContactLastName || '',
      emergencyContactRelation: p.emergency_contact_relation || p.emergencyContactRelation || '',
      emergencyContactPhone: formatUSPhoneNumber(p.emergency_contact_phone || p.emergencyContactPhone || ''),
      emergencyContactAddress: p.emergency_contact_address || p.emergencyContactAddress || '',
      email: p.email || '',
      gender: p.gender || 'Male',
      dateOfBirth: safeDateStr(p.date_of_birth || p.dateOfBirth || p.dob),
      twoWayAuth: Number(p.is_two_way_auth ?? p.two_way_auth ?? p.twoWayAuth ?? p.user?.is_two_way_auth) === 1 ? 'Enabled' : 'Disabled',
      mobileAppAccess: Number(p.mobile_app_access ?? p.user?.mobile_app_access) === 1 ? 'Enabled' : 'Disabled',
      status: patientStatusFormValue(p.status),
      timeZone: normalizeTimeZone(p.time_zone || p.timeZone || p.timezone) || '',
      rpmStartDate: safeDateStr(p.rpm_start_date || p.rpmStartDate),
      provider: String(p.provider_id || p.providerId || p.provider || ''),
      username: p.username || '',
      password: '',
      confirmPassword: '',
      systemCaregiverIds: expandCaregiverIdsFromApi(
        p.system_caregiver_ids || p.systemCaregiverIds,
        p.system_caregiver_id ?? p.systemCaregiverId
      ),
      practiceCaregiverIds: resolvePracticeCaregiverIdsFromPatient(p),
    };

    setFormData(mappedFormData);
    setPictureFile(null);
    // Load existing profile picture path for preview
    setExistingProfilePic(p.profile_pic || p.profilePic || patientToEdit.profile_pic || patientToEdit.profilePic || '');
    setPictureRemoved(false);
    setLoadedCredentials({
      username: mappedFormData.username || '',
      email: mappedFormData.email || '',
      ssn: mappedFormData.ssn || '',
    });

    // Enrolled care programs — may be array of strings, objects, or comma string
    const rawPrograms =
      p.care_programs ||
      p.carePrograms ||
      p.enrolled_programs ||
      p.enrolledPrograms ||
      [];
    let loadedPrograms = [];
    if (typeof rawPrograms === 'string') {
      loadedPrograms = rawPrograms.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    } else if (Array.isArray(rawPrograms)) {
      loadedPrograms = rawPrograms.map(item =>
        typeof item === 'string'
          ? item.trim().toLowerCase()
          : (item.short_name || item.id || item.key || '').toLowerCase()
      ).filter(Boolean);
    }
    setSelectedCarePrograms(loadedPrograms);
    setOriginalEnrolledPrograms(loadedPrograms);

    const { labels: loadedMonitoring, icdValues: loadedMonitoringIcd } = parsePatientMonitoringSelection(p);
    setSelectedMonitoring(loadedMonitoring);
    setMonitoringIcdValues(loadedMonitoringIcd);
    const patientRowId = p.id ?? p.patient_id ?? p.patient_table_id ?? null;
    const nextModes = { bp: null, bg: null, weight: null };
    [
      ['Blood Pressure', 'bp'],
      ['Blood Glucose', 'bg'],
      ['Weight', 'weight']
    ].forEach(([type, kind]) => {
      if (!loadedMonitoring.includes(type)) return;
      const storedMode = readVitalScheduleTargetMode(practiceId, patientRowId, kind);
      nextModes[kind] = storedMode === 'custom' ? 'custom' : 'practice';
    });
    setVitalModes(nextModes);
    setVitalDrafts({ bp: null, bg: null, weight: null });
    vitalConfirmedRef.current = { bp: false, bg: false, weight: false };

    const loadedConditions = normalizePatientChronicConditions(
      p.chronic_conditions || p.chronicConditions || p.icd_codes || [],
    );
    setSelectedChronicConditions(loadedConditions);

    // Meter Mappings
    let loadedMeters = [emptyMeterMappingRow()];
    if (p.meterMappings && Array.isArray(p.meterMappings)) {
      loadedMeters = p.meterMappings.length > 0 ? p.meterMappings : loadedMeters;
    } else if (p.meter_mappings && Array.isArray(p.meter_mappings)) {
      loadedMeters = p.meter_mappings.length > 0 ? p.meter_mappings : loadedMeters;
    }
    setMeterMappings(loadedMeters);

    const loadedInsurance = {
      primary: emptyInsuranceTier(),
      secondary: emptyInsuranceTier(),
    };
    const insurance = p.insurance_details || p.insurance_data || p.insuranceDetails;
    if (insurance) {
      ['primary', 'secondary'].forEach((tier) => {
        if (insurance[tier]) {
          const item = insurance[tier];
          loadedInsurance[tier] = {
            insuranceName: sanitizeInsuranceField('insuranceName', item.insuranceName || item.insurance_name || ''),
            subscriberId: sanitizeInsuranceField('subscriberId', item.subscriberId || item.subscriber_id || ''),
            insuredName: sanitizeInsuranceField('insuredName', item.insuredName || item.insured_name || ''),
            groupNo: sanitizeInsuranceField('groupNo', item.groupNo || item.group_no || item.group_number || ''),
          };
        }
      });
    }
    setInsuranceData(loadedInsurance);

    const fromApi = parsePatientConsentFromPayload(p);
    const fromStorage = readPatientConsentDraft(effectivePracticeId, excludePatientId);
    const initialConsent = buildEditModeConsentFormState({
      patient: p,
      dobValue: safeDateStr(p.date_of_birth || p.dateOfBirth || p.dob),
      homeDigits: p.cell_phone || p.cellPhone || p.home_phone || p.homePhone || '',
      formatPhoneNumberFn: formatUSPhoneNumber,
      fromApi,
      fromStorage
    });
    setConsentForm(initialConsent);
    setTermsAccepted(true);

    const loadedConsentType = p.ccm_consent_type || p.ccmConsentType || (p.ccm_consent_signature || p.consent_document_path ? 'written' : 'verbal');
    const loadedConsentSignature = p.ccm_consent_signature || p.ccmConsentSignature || p.consent_document_path || null;
    const loadedConsentSignatureSource = p.ccm_consent_signature_source || p.ccmConsentSignatureSource || (loadedConsentSignature ? 'upload' : 'draw');
    const loadedVerbalAnswersRaw = p.ccm_verbal_answers || p.ccmVerbalAnswers;
    const hasCareManagement = loadedPrograms.includes('ccm') || loadedPrograms.includes('pcm');
    const loadedVerbalAnswers = Array.isArray(loadedVerbalAnswersRaw) && loadedVerbalAnswersRaw.length >= 3
      ? [0, 1, 2].map((index) => Boolean(loadedVerbalAnswersRaw[index]))
      : (loadedConsentType === 'verbal' && hasCareManagement ? [true, true, true] : [false, false, false]);
    const loadedConsentDate = p.ccm_consent_date || p.ccmConsentDate || p.ccm_verbal_consent_date || null;

    setCcmConsentType(loadedConsentType);
    setCcmConsentSignature(loadedConsentSignature);
    setCcmConsentSignatureSource(loadedConsentSignatureSource);
    setCcmVerbalAnswers(loadedVerbalAnswers);
    if (loadedConsentDate) {
      setCcmVerbalConsentDate(String(loadedConsentDate).split('T')[0]);
    }

    setInitialEditSnapshot(JSON.stringify({
      formData: mappedFormData,
      selectedCarePrograms: loadedPrograms,
      selectedMonitoring: loadedMonitoring,
      monitoringIcdValues: loadedMonitoringIcd,
      selectedChronicConditions: loadedConditions,
      ccmRestrictionsConfirmed: false,
      meterMappings: loadedMeters,
      insuranceData: loadedInsurance,
      ccmConsentType: loadedConsentType,
      ccmConsentSignature: loadedConsentSignature,
      ccmVerbalAnswers: loadedVerbalAnswers,
    }));

  }, [patientToEdit]);
  // ────────────────────────────────────────────────────────────────────────────

  // Load configuration details on mount
  useEffect(() => {
    if (effectivePracticeId) {
      dispatch(fetchSystemCaregivers(effectivePracticeId));
      dispatch(fetchPracticeProviders({ practiceId: effectivePracticeId, force: true }));
      dispatch(fetchPracticeCaregivers({ practiceId: effectivePracticeId, force: true }));
      dispatch(fetchPractice(effectivePracticeId));
    }

    // Fetch care programs from database
    careProgramService.getCarePrograms()
      .then(res => {
        if (res && res.success && Array.isArray(res.data)) {
          setCarePrograms(filterActiveCarePrograms(res.data));
        } else {
          setCarePrograms([]);
        }
      })
      .catch(() => {
        setCarePrograms([]);
      });

    inventoryService.getProducts({ page: 1, limit: 999 }).then(res => {
      if (res && res.success) setProducts(res.data);
    }).catch(() => { });

    lookupService.getIcdCodes()
      .then((res) => {
        if (res?.success && Array.isArray(res.data)) {
          setIcdCodesByMeasureType(groupIcdCodesByMeasureType(res.data));
        }
      })
      .catch(() => {
        setIcdCodesByMeasureType({ 1: [], 2: [], 3: [] });
      });
  }, [dispatch, effectivePracticeId]);

  const availableCarePrograms = useMemo(() => {
    const enrolled = currentPractice?.enrolled_programs;
    if (Array.isArray(enrolled) && enrolled.length > 0) {
      return buildProgramTabsFromEnrolled(enrolled).map((tab) => ({
        ...tab,
        short_name: tab.short_name,
        full_name: tab.full_name,
        badge_color: tab.color
      }));
    }
    return getProgramsForPractice(carePrograms, enrolled);
  }, [carePrograms, currentPractice?.enrolled_programs]);

  const practiceHasPcm = useMemo(
    () => practiceHasProgram(availableCarePrograms, 'pcm'),
    [availableCarePrograms]
  );
  const practiceHasCcm = useMemo(
    () => practiceHasProgram(availableCarePrograms, 'ccm'),
    [availableCarePrograms]
  );

  const syncCareManagementFromConditions = (nextConditions) => {
    const count = Array.isArray(nextConditions) ? nextConditions.length : 0;
    if (count >= 1) {
      setErrors((prev) => {
        if (!prev.chronicConditions) return prev;
        const next = { ...prev };
        delete next.chronicConditions;
        return next;
      });
    }
  };

  useEffect(() => {
    if (availableCarePrograms.length === 0) return;
    // Only clean up any invalid selections the user has already made
    setSelectedCarePrograms((prev) => {
      if (prev.length === 0) return prev; // already empty, nothing to clean
      const valid = prev.filter((key) =>
        availableCarePrograms.some((prog) => normalizeProgramKey(prog.short_name) === key)
      );
      return valid;
    });
  }, [availableCarePrograms]);

  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = formatUSPhoneNumber;

  // Helper for SSN formatting XXX-XX-XXXX
  const formatSSN = (value) => {
    if (!value) return value;
    const ssn = value.replace(/[^\d]/g, '');
    const ssnLength = ssn.length;
    if (ssnLength < 4) return ssn;
    if (ssnLength < 6) {
      return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
    }
    return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'status' && patientToEdit) {
      const p = patientToEdit.patient || patientToEdit;
      const initialStatusId = normalizePatientStatusId(p?.status);
      const targetStatusId = normalizePatientStatusId(value);
      const hasAssignedDevices = meterMappings.some(isCompleteMeterFormRow);

      if (initialStatusId === 2 && (targetStatusId === 3 || targetStatusId === 4) && hasAssignedDevices) {
        setStatusDeviceRemovalModal({
          show: true,
          targetStatus: value
        });
        return;
      }
    }

    // Phone Number Masking
    if (['homePhone', 'cellPhone', 'fax', 'emergencyContactPhone'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : name === 'emergencyContactPhone' ? 'Emergency Contact Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // No special characters for names and city
    if (['firstName', 'lastName', 'middleInitial', 'city'].includes(name)) {
      if (name === 'middleInitial') {
        if (value && !/^[a-zA-Z\s]*$/.test(value)) return;
        if (value.length > 20) return;
      }
      const cleanedValue = name === 'middleInitial'
        ? value
        : value.replace(/[^a-zA-Z\s]/g, '');
      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
      updateFieldErrorOnChange(setErrors, name, cleanedValue);
      return;
    }

    // Address lines - allow alphanumeric and basic punctuation like spaces, hash, comma, hyphen
    if (['addressLine1', 'addressLine2'].includes(name)) {
      const cleanedValue = value.replace(/[^a-zA-Z0-9\s#,\-.]/g, '');
      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
      updateFieldErrorOnChange(setErrors, name, cleanedValue);
      return;
    }

    // Zip Code Validation (Prevention & length check)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        setErrors(prev => {
          const next = { ...prev };
          if (numericValue.length > 0 && numericValue.length < 5) {
            next.zipCode = 'Zip code must be exactly 5 digits';
          } else {
            delete next.zipCode;
          }
          return next;
        });
      }
      return;
    }

    // SSN Masking + uniqueness check
    if (name === 'ssn') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 9) {
        const formatted = formatSSN(numericValue);
        setFormData((prev) => ({ ...prev, ssn: formatted }));
        updateFieldErrorOnChange(setErrors, 'ssn', formatted.length === 11 ? formatted : '');
        scheduleSsnCheck(formatted, setErrors);
      }
      return;
    }

    // Username validation
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData((prev) => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      setErrors((prev) => {
        const next = { ...prev };
        if (!cleanedValue) {
          next.username = 'Username is required';
        } else if (formatError) {
          next.username = formatError;
        } else {
          delete next.username;
        }
        return next;
      });
      if (cleanedValue && !formatError) {
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    // Email Validation
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      setFormData((prev) => ({ ...prev, email: cleanedValue }));
      const emailErr = validateRequiredEmail(cleanedValue);
      setErrors((prev) => {
        const next = { ...prev };
        if (emailErr) next.email = emailErr;
        else delete next.email;
        return next;
      });
      if (!emailErr && cleanedValue) {
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      return;
    }

    // Password Validation (Real-time)
    if (name === 'password') {
      setFormData((prev) => ({ ...prev, password: value }));
      if (!value) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.password;
          return next;
        });
      } else {
        const validation = validatePassword(value, {
          firstName: formData.firstName,
          lastName: formData.lastName,
        });
        setErrors((prev) => {
          const next = { ...prev };
          if (!validation.isValid) next.password = validation.error;
          else delete next.password;
          return next;
        });
      }
      return;
    }

    if (name === 'confirmPassword') {
      setFormData((prev) => ({ ...prev, confirmPassword: value }));
      setErrors((prev) => {
        const next = { ...prev };
        if (!value && !patientToEdit) {
          next.confirmPassword = 'Confirm Password is required';
        } else if (formData.password && value !== formData.password) {
          next.confirmPassword = 'Passwords do not match. Please re-enter.';
        } else {
          delete next.confirmPassword;
        }
        return next;
      });
      return;
    }

    if (name === 'state') {
      const stateId = getStateIdFromName(value);
      setFormData((prev) => ({ ...prev, state: value, stateId }));
      updateFieldErrorOnChange(setErrors, 'state', value);
      return;
    }

    if (name === 'rpmStartDate') {
      setFormData((prev) => ({ ...prev, rpmStartDate: value }));
      updateFieldErrorOnChange(setErrors, 'rpmStartDate', value, {
        required: true,
        requiredMessage: 'RPM Start Date is required',
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    updateFieldErrorOnChange(setErrors, name, value);
  };

  const addSystemCaregiverId = (idStr) => {
    setFormData((prev) => {
      if (prev.systemCaregiverIds.includes(idStr)) return prev;
      return { ...prev, systemCaregiverIds: [...prev.systemCaregiverIds, idStr] };
    });
    setErrors((prev) => {
      if (!prev.systemCaregiver) return prev;
      const next = { ...prev };
      delete next.systemCaregiver;
      return next;
    });
  };

  const removeSystemCaregiverId = (idStr) => {
    setFormData((prev) => ({
      ...prev,
      systemCaregiverIds: prev.systemCaregiverIds.filter((id) => id !== idStr),
    }));
  };

  const addPracticeCaregiverId = (idStr) => {
    setFormData((prev) => {
      if (prev.practiceCaregiverIds.includes(idStr)) return prev;
      return { ...prev, practiceCaregiverIds: [...prev.practiceCaregiverIds, idStr] };
    });
    setErrors((prev) => {
      if (!prev.practiceCaregiver) return prev;
      const next = { ...prev };
      delete next.practiceCaregiver;
      return next;
    });
  };

  const removePracticeCaregiverId = (idStr) => {
    setFormData((prev) => ({
      ...prev,
      practiceCaregiverIds: prev.practiceCaregiverIds.filter((id) => id !== idStr),
    }));
  };

  const pickProviderField = (name, idStr) => {
    setFormData((prev) => ({ ...prev, [name]: idStr }));
    setErrors((prev) => {
      if (name !== 'provider' || !prev.provider) return prev;
      const next = { ...prev };
      delete next.provider;
      return next;
    });
  };

  const resetCareManagementSelection = () => {
    setSelectedChronicConditions([]);
    setCcmRestrictionsConfirmed(false);
    setCcmConsentSignature(null);
    setCcmConsentSignatureSource('draw');
    setCcmVerbalAnswers([false, false, false]);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.chronicConditions;
      delete next.ccmRestrictions;
      delete next.ccmConsent;
      delete next.ccmConsentSignature;
      return next;
    });
  };

  const handleCareProgramToggle = (programKey, progObj) => {
    if (selectedCarePrograms.includes(programKey)) {
      if (patientToEdit && originalEnrolledPrograms.includes(programKey)) {
        setProgramConfirmModal({ isOpen: true, programKey, programObj: progObj });
      } else {
        setSelectedCarePrograms(prev => prev.filter(p => p !== programKey));
        if (programKey === 'ccm' || programKey === 'pcm') {
          resetCareManagementSelection();
        }
      }
    } else {
      setSelectedCarePrograms((prev) => {
        let next = [...prev, programKey];
        if (programKey === 'pcm') next = next.filter((p) => p !== 'ccm');
        if (programKey === 'ccm') next = next.filter((p) => p !== 'pcm');
        return next;
      });
      if (programKey === 'ccm' || programKey === 'pcm') {
        resetCareManagementSelection();
      }
      if (errors.carePrograms) {
        clearFieldError(setErrors, 'carePrograms');
      }
    }
  };

  const confirmRemoveProgram = () => {
    const key = programConfirmModal.programKey;
    setSelectedCarePrograms(prev => prev.filter(p => p !== key));
    if (key === 'ccm' || key === 'pcm') {
      resetCareManagementSelection();
    }
    setProgramConfirmModal({ isOpen: false, programKey: null, programObj: null });
  };

  const handleMonitoringToggle = (type) => {
    if (!availableMonitoringTypes.includes(type) && selectedMonitoring.includes(type)) return;
    const kind = monitoringVitalKind(type);
    if (kind && !selectedMonitoring.includes(type)) {
      setVitalModal(kind);
      return;
    }
    setSelectedMonitoring((prev) => {
      const isSelected = prev.includes(type);
      if (isSelected) {
        setMonitoringIcdValues((icdPrev) => ({ ...icdPrev, [type]: '' }));
        if (kind) {
          setVitalModes((modes) => ({ ...modes, [kind]: null }));
          setVitalDrafts((drafts) => ({ ...drafts, [kind]: null }));
          vitalConfirmedRef.current[kind] = false;
        }
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  };

  const handleVitalSettingsCancel = () => {
    setVitalModal(null);
  };

  const handleVitalSettingsConfirm = ({ mode, payload }) => {
    const kind = vitalModal;
    if (!kind) return;
    vitalConfirmedRef.current[kind] = true;
    setVitalModes((modes) => ({ ...modes, [kind]: mode }));
    setVitalDrafts((drafts) => ({ ...drafts, [kind]: mode === 'custom' ? payload : null }));
    const type = kind === 'bg' ? 'Blood Glucose' : kind === 'weight' ? 'Weight' : 'Blood Pressure';
    setSelectedMonitoring((prev) => (prev.includes(type) ? prev : [...prev, type]));
    setVitalModal(null);
  };

  const handleMonitoringIcdChange = (type, value) => {
    setMonitoringIcdValues((prev) => ({ ...prev, [type]: value }));
  };

  const handleMeterChange = (index, field, value) => {
    setMeterMappings((prev) =>
      prev.map((meter, i) => {
        if (i !== index) return meter;
        return {
          ...meter,
          [field]: field === 'serialNumber' ? normalizeDeviceSerialInput(value) : value
        };
      })
    );
  };
  const handleAddMeter = () => {
    const lastRow = meterMappings[meterMappings.length - 1];
    if (!isCompleteMeterFormRow(lastRow)) {
      toast.warn('Please complete the current device before adding another.');
      return;
    }
    setMeterMappings((prev) => [...prev, emptyMeterMappingRow()]);
  };

  const handleRemoveMeter = (index) => {
    if (meterMappings.length <= 1) return;
    if (index === meterMappings.length - 1) return;
    setMeterMappings((prev) => prev.filter((_, i) => i !== index));
  };

  const handleInsuranceChange = (tier, field, value) => {
    const sanitizedValue = sanitizeInsuranceField(field, value);
    setInsuranceData((prev) => ({
      ...prev,
      [tier]: { ...prev[tier], [field]: sanitizedValue },
    }));
    const errKey = insuranceErrorKey(tier, field);
    const liveError = getInsuranceFieldError(field, sanitizedValue);
    setErrors((prev) => {
      const next = { ...prev };
      if (liveError) next[errKey] = liveError;
      else delete next[errKey];
      return next;
    });
  };

  const handleInsuranceBlur = (tier, field) => {
    const data = insuranceData[tier] || {};
    const value =
      field === 'insuranceName'
        ? data.insuranceName || data.insurance_name || ''
        : field === 'subscriberId'
          ? data.subscriberId || data.subscriber_id || ''
          : field === 'insuredName'
            ? data.insuredName || data.insured_name || ''
            : data.groupNo || data.group_no || data.group_number || '';
    const errKey = insuranceErrorKey(tier, field);
    const liveError = getInsuranceFieldError(field, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (liveError) next[errKey] = liveError;
      else delete next[errKey];
      return next;
    });
  };

  const validateAllForm = () => {
    const newErrors = {};
    const isEditMode = !!patientToEdit;

    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }

    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of Birth is required';
    else if (new Date(formData.dateOfBirth) > new Date()) newErrors.dateOfBirth = 'Cannot be future date';

    if (formData.ssn && formData.ssn.trim().length > 0 && formData.ssn.length !== 11) {
      newErrors.ssn = 'Valid 9-digit SSN required';
    }

    const homePhoneError = validateRequiredPhone(formData.homePhone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.homePhone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    const emergencyPhoneError = validateRequiredPhone(formData.emergencyContactPhone, { required: false, label: 'Emergency Contact Phone' });
    if (emergencyPhoneError) newErrors.emergencyContactPhone = emergencyPhoneError;

    const emailError = validateRequiredEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state?.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.trim().length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }
    if (!formData.timeZone?.trim()) newErrors.timeZone = 'Time Zone is required';

    if (!formData.provider) newErrors.provider = 'Provider is required';
    if (requiresSystemCaregiver && (!formData.systemCaregiverIds || formData.systemCaregiverIds.length === 0)) {
      newErrors.systemCaregiver = 'Select at least one system caregiver';
    }
    if (!formData.practiceCaregiverIds || formData.practiceCaregiverIds.length === 0) {
      newErrors.practiceCaregiver = 'Select at least one practice caregiver';
    }

    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const validation = validatePassword(formData.password, {
          firstName: formData.firstName,
          lastName: formData.lastName
        });
        if (!validation.isValid) newErrors.password = validation.error;
      }
      if (!formData.confirmPassword?.trim()) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
      }
    } else if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
    }

    if (selectedCarePrograms.length === 0) {
      newErrors.carePrograms = 'Please select at least one Care Program.';
    }

    if (selectedCarePrograms.includes('rpm')) {
      if (!String(formData.rpmStartDate || '').trim()) {
        newErrors.rpmStartDate = 'RPM Start Date is required';
      } else if (new Date(formData.rpmStartDate) > new Date()) {
        newErrors.rpmStartDate = 'RPM Start Date cannot be in the future';
      }
      if (selectedMonitoring.length === 0) {
        newErrors.monitoringTypes = 'Please select at least one Type of Monitoring for RPM.';
      }
      if (!termsAccepted) {
        newErrors.terms = 'You must accept the terms and conditions';
      } else if (!isEditMode) {
        if (!consentForm.caregiverDate) {
          newErrors.terms = `${CONSENT_DATE_LABELS.caregiverDate} is required on the consent form.`;
        } else if (isConsentDateBeforePracticeCreation(consentForm.caregiverDate)) {
          newErrors.terms = `${CONSENT_DATE_LABELS.caregiverDate} cannot be earlier than the practice creation date.`;
        } else if (consentForm.patientDate && isConsentDateBeforePracticeCreation(consentForm.patientDate)) {
          newErrors.terms = `${CONSENT_DATE_LABELS.patientDate} cannot be earlier than the practice creation date.`;
        } else if (consentForm.clinicianDate && isConsentDateBeforePracticeCreation(consentForm.clinicianDate)) {
          newErrors.terms = `${CONSENT_DATE_LABELS.clinicianDate} cannot be earlier than the practice creation date.`;
        }
      }
      Object.assign(newErrors, documentsSectionRef.current?.validateDocuments?.() || {});
    }

    if (selectedCarePrograms.includes('ccm')) {
      if (selectedChronicConditions.length < 2) {
        newErrors.chronicConditions = practiceHasPcm && selectedChronicConditions.length === 1
          ? 'CCM requires at least two chronic conditions. Use PCM for a single high-risk condition.'
          : 'Please select at least two chronic disease conditions for CCM.';
      }
      const requireCcmBillingRestrictions =
        !isEditMode || !originalEnrolledPrograms.includes('ccm');
      if (requireCcmBillingRestrictions && !ccmRestrictionsConfirmed) {
        newErrors.ccmRestrictions = 'Please confirm CCM Billing Restrictions.';
      }
      if (ccmConsentType === 'written') {
        const signatureError = getConsentSignatureValidationError(ccmConsentSignature);
        if (signatureError) {
          newErrors.ccmConsentSignature = signatureError;
        }
      }
    }

    if (selectedCarePrograms.includes('pcm')) {
      if (selectedChronicConditions.length !== 1) {
        newErrors.chronicConditions = selectedChronicConditions.length > 1
          ? 'PCM allows one principal chronic condition only.'
          : 'Please select one high-risk chronic condition for PCM.';
      }
      const requirePcmBillingRestrictions =
        !isEditMode || !originalEnrolledPrograms.includes('pcm');
      if (requirePcmBillingRestrictions && !ccmRestrictionsConfirmed) {
        newErrors.ccmRestrictions = 'Please confirm PCM Billing Restrictions.';
      }
      if (ccmConsentType === 'written') {
        const signatureError = getConsentSignatureValidationError(ccmConsentSignature);
        if (signatureError) {
          newErrors.ccmConsentSignature = signatureError;
        }
      }
    }

    // Health Insurance Validation
    Object.assign(newErrors, validateInsuranceData(insuranceData));

    const meterError = validateMeterMappingList(meterMappings);
    if (meterError) {
      newErrors.meterMappings = meterError;
    }

    if (pictureFile) {
      const pictureError = validateImageUploadFile(pictureFile, { label: 'Profile picture' });
      if (pictureError) newErrors.profilePicture = pictureError;
    }

    return newErrors;
  };

  const navigateToFirstErrorStep = (validationErrors) => {
    const keys = Object.keys(validationErrors);
    if (keys.some((key) => PATIENT_DEMOGRAPHIC_ERROR_KEYS.has(key))) {
      const demographicsIndex = designCSteps.findIndex((step) => step === 'Demographics');
      if (demographicsIndex >= 0) setCurrentStep(demographicsIndex);
      return;
    }
    if (validationErrors.carePrograms) {
      const programsIndex = designCSteps.findIndex((step) => step === 'Programs');
      if (programsIndex >= 0) setCurrentStep(programsIndex);
      return;
    }
    if (
      validationErrors.rpmStartDate ||
      validationErrors.monitoringTypes ||
      validationErrors.terms ||
      keys.some((key) => key.startsWith('documentFile')) ||
      keys.some((key) => isInsuranceErrorKey(key))
    ) {
      const rpmIndex = designCSteps.findIndex((step) => step.toLowerCase() === 'rpm');
      if (rpmIndex >= 0) setCurrentStep(rpmIndex);
      return;
    }
    if (validationErrors.chronicConditions || validationErrors.ccmRestrictions || validationErrors.ccmConsent || validationErrors.ccmConsentSignature) {
      const careMgmtIndex = designCSteps.findIndex((step) => {
        const name = String(step || '').toLowerCase();
        return name === 'ccm' || name === 'pcm';
      });
      if (careMgmtIndex >= 0) setCurrentStep(careMgmtIndex);
    }
  };

  const handleUsernameBlur = (e) => {
    scheduleUsernameBlurCheck(e?.target?.value ?? formData.username, setErrors);
  };
  const handleEmailBlur = (e) => {
    const value = String(e?.target?.value ?? formData.email ?? '').trim();
    const emailErr = validateRequiredEmail(value);
    if (!emailErr && value) {
      scheduleEmailBlurCheck(value, setErrors, 'email');
    }
  };

  const handleSsnBlur = () => {
    const value = String(formData.ssn || '').trim();
    if (value.replace(/\D/g, '').length === 9) {
      scheduleSsnCheck(value, setErrors);
    }
  };

  const verifyDemographicAvailability = async () => {
    console.log('[AddPatientModal] verifyDemographicAvailability starting...', { checking, errors, ssn: formData.ssn });
    if (checking.username || checking.email || checking.ssn) {
      console.warn('[AddPatientModal] Availability check currently in progress:', checking);
      toast.warn('Please wait while username, email, and SSN availability are verified.');
      return false;
    }
    if (hasAvailabilityConflict(errors)) {
      console.warn('[AddPatientModal] Existing availability conflict in errors:', errors);
      return false;
    }
    const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
    const emailOk = await ensureEmailAvailable(formData.email, setErrors);
    const ssnOk = await ensureSsnAvailable(formData.ssn, setErrors);
    console.log('[AddPatientModal] Availability check results:', { usernameOk, emailOk, ssnOk });
    return usernameOk && emailOk && ssnOk;
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const validationErrors = preserveAvailabilityErrors(errors, validateAllForm());
      // Care-program (and related program-step) errors already show inline in the modal.
      const inlineProgramErrorKeys = new Set([
        'carePrograms',
        'monitoringTypes',
        'rpmStartDate',
        'terms',
        'chronicConditions',
        'ccmRestrictions',
        'ccmConsent',
        'ccmConsentSignature',
      ]);
      const errorKeys = Object.keys(validationErrors);
      const onlyInlineProgramErrors =
        errorKeys.length > 0 && errorKeys.every((key) => inlineProgramErrorKeys.has(key));
      if (!reportValidationErrors(setErrors, validationErrors, onlyInlineProgramErrors ? null : toast, errors)) {
        navigateToFirstErrorStep(validationErrors);
        setIsSubmitting(false);
        return;
      }

      const availabilityOk = await verifyDemographicAvailability();
      if (!availabilityOk) {
        navigateToFirstErrorStep(errors);
        setIsSubmitting(false);
        return;
      }

      const persistScheduleChoice = async (patientRowId) => {
        if (!selectedCarePrograms.includes('rpm') || !patientRowId) return;
        const selectedKinds = [
          ['Blood Pressure', 'bp'],
          ['Blood Glucose', 'bg'],
          ['Weight', 'weight']
        ].filter(([type]) => selectedMonitoring.includes(type));
        if (!selectedKinds.some(([, kind]) => vitalConfirmedRef.current[kind])) return;
        const anyCustom = selectedKinds.some(([, kind]) => vitalModes[kind] === 'custom');
        const changedPayload = {};
        selectedKinds.forEach(([, kind]) => {
          if (vitalModes[kind] === 'custom' && vitalDrafts[kind]) {
            Object.assign(changedPayload, vitalDrafts[kind]);
          }
          if (vitalModes[kind] === 'practice' || vitalModes[kind] === 'custom') {
            writeVitalScheduleTargetMode(practiceId, patientRowId, kind, vitalModes[kind]);
          }
        });
        try {
          await applyEnrollmentScheduleChoice({
            practiceId,
            patientId: patientRowId,
            usePracticeSettings: !anyCustom,
            changedPayload: anyCustom && Object.keys(changedPayload).length ? changedPayload : null
          });
        } catch (scheduleErr) {
          toast.warning(
            scheduleErr?.message ||
              'Patient was saved, but the custom vital settings could not be saved. You can update them on the patient page.'
          );
        }
      };

      const isEditMode = !!(patientToEdit);
      const baseData = { ...formData };
      if (isEditMode && !baseData.password) {
        delete baseData.password;
        delete baseData.confirmPassword;
      }

      const systemCaregiverIdsNum = requiresSystemCaregiver
        ? toNumericCaregiverIds(formData.systemCaregiverIds)
        : [];
      const practiceCaregiverIdsNum = toNumericCaregiverIds(formData.practiceCaregiverIds);

      const restrictionsConfirmedForSubmit =
        (selectedCarePrograms.includes('ccm') || selectedCarePrograms.includes('pcm')) &&
        (ccmRestrictionsConfirmed
          || (isEditMode && (originalEnrolledPrograms.includes('ccm') || originalEnrolledPrograms.includes('pcm'))));

      const patientPayload = {
        ...baseData,
        phone: (formData.cellPhone || '').replace(/\D/g, ''),
        cell_phone: (formData.homePhone || '').replace(/\D/g, ''),
        homePhone: (formData.cellPhone || '').replace(/\D/g, ''),
        cellPhone: (formData.homePhone || '').replace(/\D/g, ''),
        is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
        twoWayAuth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
        mobile_app_access: formData.mobileAppAccess === 'Enabled' ? 1 : 0,
        state_id: formData.stateId ?? getStateIdFromName(formData.state),
        practice_id: parseInt(practiceId),
        care_programs: selectedCarePrograms,
        monitoring_types: selectedCarePrograms.includes('rpm')
          ? buildMonitoringTypesPayload(selectedMonitoring, monitoringIcdValues)
          : [],
        chronic_conditions: serializePatientChronicConditions(selectedChronicConditions),
        ccm_restrictions_confirmed: restrictionsConfirmedForSubmit,
        practice_caregiver_id: practiceCaregiverIdsNum.length > 0 ? String(practiceCaregiverIdsNum[0]) : '',
        practice_caregiver_ids: practiceCaregiverIdsNum,
        practiceCaregiverIds: practiceCaregiverIdsNum,
        system_caregiver_id: systemCaregiverIdsNum.length > 0 ? String(systemCaregiverIdsNum[0]) : '',
        system_caregiver_ids: systemCaregiverIdsNum,
        systemCaregiverIds: systemCaregiverIdsNum,
        agreement: termsAccepted ? 1 : 0,
        termsAndConditions: termsAccepted,
        ccm_consent_data: ((selectedCarePrograms.includes('ccm') || selectedCarePrograms.includes('pcm')) ? {
          type: ccmConsentType,
          program: selectedCarePrograms.includes('pcm') ? 'pcm' : 'ccm',
          verbal_answers: ccmVerbalAnswers,
          restrictions_confirmed: restrictionsConfirmedForSubmit,
          ...(ccmConsentType === 'written' ? {
            signature: ccmConsentSignature,
            signature_source: ccmConsentSignatureSource
          } : {})
        } : undefined),
        ccm_consent_signature: ccmConsentType === 'written' ? ccmConsentSignature : null,
        ccm_consent_type: ccmConsentType,
        ccm_consent_signature_source: ccmConsentSignatureSource,
        consent_data: selectedCarePrograms.includes('rpm') ? consentForm : ((selectedCarePrograms.includes('ccm') || selectedCarePrograms.includes('pcm')) ? {
          type: ccmConsentType,
          program: selectedCarePrograms.includes('pcm') ? 'pcm' : 'ccm',
          verbal_answers: ccmVerbalAnswers,
          restrictions_confirmed: restrictionsConfirmedForSubmit,
          ...(ccmConsentType === 'written' ? {
            signature: ccmConsentSignature,
            signature_source: ccmConsentSignatureSource
          } : {})
        } : undefined),
        meter_mappings: meterMappings.filter(isCompleteMeterFormRow),
        insurance_data: {
          primary: insuranceData.primary,
          secondary: insuranceData.secondary,
        }
      };

      // Build FormData so we can include the profile picture file
      const patientFD = new FormData();
      if (pictureFile) {
        patientFD.append('profilePicture', pictureFile);
      } else if (pictureRemoved) {
        patientFD.append('profile_pic', '');
        patientFD.append('clear_profile_pic', '1');
      }
      Object.entries(patientPayload).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          patientFD.append(key, '');
        } else if (typeof value === 'object' && !(value instanceof File)) {
          patientFD.append(key, JSON.stringify(value));
        } else {
          patientFD.append(key, String(value));
        }
      });

      if (isEditMode) {
        // Resolve ID — API may return { patient: { id } } or { id } directly
        const p = patientToEdit.patient || patientToEdit;
        const patientId = p.id || p.patient_id || p.patient_table_id;
        if (!patientId) {
          toast.error('Cannot update: patient ID is missing.');
          setIsSubmitting(false);
          return;
        }
        await dispatch(updatePatient({ practiceId, patientId, patientData: patientFD })).unwrap();
        await patientService.updatePatientPrograms(practiceId, patientId, { enrolled_programs: selectedCarePrograms });
        await persistScheduleChoice(patientId);

        let docUploadResult = { uploaded: 0, failed: 0 };
        if (selectedCarePrograms.includes('rpm') && documentsSectionRef.current) {
          docUploadResult = await documentsSectionRef.current.uploadPendingDocuments(patientId);
        }

        if (docUploadResult.failed > 0) {
          toast.warning(
            `Patient was updated, but ${docUploadResult.failed} document file(s) could not be uploaded. Fix the rows marked below and try again.`
          );
          setIsSubmitting(false);
          return;
        }

        toast.success('Patient updated successfully');
        savePatientConsentDraft(effectivePracticeId, patientId, consentForm);
      } else {
        const createResult = await dispatch(createPatient({ practiceId, patientData: patientFD })).unwrap();
        const newPatientRowId =
          createResult?.data?.patient_table_id ??
          createResult?.patient_table_id ??
          createResult?.data?.id ??
          null;

        if (newPatientRowId) {
          savePatientConsentDraft(effectivePracticeId, newPatientRowId, consentForm);
          await persistScheduleChoice(newPatientRowId);
        }

        let docUploadResult = { uploaded: 0, failed: 0 };
        if (
          selectedCarePrograms.includes('rpm') &&
          newPatientRowId &&
          documentsSectionRef.current
        ) {
          docUploadResult = await documentsSectionRef.current.uploadPendingDocuments(newPatientRowId);
        } else if (
          selectedCarePrograms.includes('rpm') &&
          !newPatientRowId &&
          (documentsSectionRef.current?.getPendingUploads?.() || []).length > 0
        ) {
          toast.warning(
            'Patient was created but document uploads were skipped because the server did not return a patient id. Open the patient and upload documents manually.'
          );
        }

        if (docUploadResult.failed > 0) {
          toast.warning(
            `Patient was created, but ${docUploadResult.failed} document file(s) could not be saved. Fix the document rows and try again.`
          );
          setIsSubmitting(false);
          return;
        }

        toast.success('Patient saved successfully');
      }
      if (onSave) onSave();
    } catch (error) {
      console.error('Submission error:', error);
      let apiMsg = '';
      if (typeof error === 'string') {
        apiMsg = error;
      } else {
        apiMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (Array.isArray(error?.response?.data?.errors) ? error.response.data.errors[0] : null) ||
          error?.message ||
          '';
      }
      const lower = apiMsg.toLowerCase();
      let hasUserErr = false;
      let hasEmailErr = false;

      if (lower.includes('username') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate') || lower.includes('in use') || lower.includes('already'))) {
        setErrors(prev => ({ ...prev, username: 'This username is already taken. Please choose a different username.' }));
        hasUserErr = true;
      }
      if (lower.includes('email') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate') || lower.includes('in use') || lower.includes('already'))) {
        setErrors(prev => ({ ...prev, email: 'This email address is already registered. Please use a different email.' }));
        hasEmailErr = true;
      }

      if (applyPasswordServerFieldError(setErrors, apiMsg)) {
        navigateToFirstErrorStep({ password: apiMsg });
        return;
      }

      if (hasUserErr || hasEmailErr) {
        navigateToFirstErrorStep({
          ...(hasUserErr ? { username: 'taken' } : {}),
          ...(hasEmailErr ? { email: 'taken' } : {}),
        });
      }

      let friendlyMsg = '';
      if (hasUserErr && hasEmailErr) {
        friendlyMsg = 'This username is already taken and this email address is already registered. Please use different values.';
      } else if (hasUserErr) {
        friendlyMsg = 'This username is already taken. Please choose a different username.';
      } else if (hasEmailErr) {
        friendlyMsg = 'This email address is already registered. Please use a different email.';
      } else {
        friendlyMsg = apiMsg || `Failed to ${patientToEdit ? 'update' : 'add'} patient. Please try again.`;
      }
      if (/foracare/i.test(friendlyMsg)) {
        friendlyMsg = patientToEdit
          ? 'Unable to update the patient. Please check the device details and try again.'
          : 'Unable to save the patient. Please check the device details and try again.';
      }
      toast.error(friendlyMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Stepper calculations — program steps always follow RPM then CCM order
  const orderedSelectedPrograms = sortProgramKeysForDisplay(selectedCarePrograms);
  const designCSteps = ['Demographics', 'Programs', ...orderedSelectedPrograms.map((programKey) => String(programKey || '').toUpperCase())];
  const activeStepName = designCSteps[currentStep]?.toUpperCase();
  const activeProgramStepKey = orderedSelectedPrograms.find((key) => key.toUpperCase() === activeStepName);

  // Focus trap - modal is always open when rendered
  const modalRef = useRef(null);
  const handleRequestClose = () => {
    if (isSubmitting) return;
    onClose?.();
  };
  const handleEscapeKeyDown = (event) => {
    if (document.querySelector('.bp-enroll-overlay')) return;
    createModalEscapeKeyDownHandler(handleRequestClose)(event);
  };
  useFocusTrap(modalRef, true, handleRequestClose);

  // Lock background page scroll while enroll/edit modal is open
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const isInsideModal = (target) => {
      if (!(target instanceof Node)) return false;
      if (modalRef.current?.contains(target)) return true;
      if (target.closest?.('.icd-search-dropdown, .pcc-form-overlay, .react-datepicker, .react-datepicker-popper, #app-date-input-portal, .bp-enroll-overlay, .practices-list-modal-overlay, .practices-list-modal-container, .practices-list-modal-body, .event-log-section-container')) return true;
      return Boolean(target.closest?.('.pif-modal-content, .modal.show, .add-patient-modal-content, .add-patient-modal-overlay'));
    };

    const preventBackgroundScroll = (event) => {
      if (isInsideModal(event.target)) return;
      event.preventDefault();
    };

    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.paddingRight = previousBodyPaddingRight;
      document.removeEventListener('wheel', preventBackgroundScroll);
      document.removeEventListener('touchmove', preventBackgroundScroll);
    };
  }, []);

  const renderProgramStepContent = (programKey) => {
    switch (programKey) {
      case 'rpm':
        return renderRPMStep();
      case 'ccm':
        return renderCCMStep();
      case 'pcm':
        return renderPCMStep();
      default: {
        const prog = availableCarePrograms.find((p) => normalizeProgramKey(p.short_name) === programKey);
        return renderPlaceholderStep(prog?.full_name || prog?.short_name || programKey.toUpperCase());
      }
    }
  };

  return (
    <>
      <div
        className="pif-modal-overlay"
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
        aria-hidden="true"
      />
      <div className="pif-modal-content" ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleEscapeKeyDown}>
        <div className="pif-container">
          <div className="pif-stepper-header">
            <button
              type="button"
              className="pif-modal-close-btn"
              onClick={handleRequestClose}
              disabled={isSubmitting}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h2 className="pif-stepper-title">{patientToEdit ? "Edit patient" : "Let's enroll the patient"}</h2>

            <div className="pif-stepper-wrapper">
              {designCSteps.map((step, idx) => {
                const isCompleted = currentStep > idx;
                const isActiveStep = currentStep === idx;

                return (
                  <React.Fragment key={idx}>
                    {idx > 0 && (
                      <div className={`pif-stepper-line ${isCompleted || isActiveStep ? 'active' : ''}`}></div>
                    )}
                    <div className="pif-stepper-item">
                      <div className={`pif-stepper-circle ${isCompleted ? 'completed' : isActiveStep ? 'active' : 'future'}`}>
                        {isCompleted && <Check size={14} strokeWidth={3} className="text-white" />}
                        {isActiveStep && <div className="pif-stepper-circle-inner"></div>}
                      </div>
                      <span className={`pif-stepper-label ${isCompleted || isActiveStep ? 'active' : ''}`}>{step}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="pif-content-wrapper">
            <div className="pif-content">
              {activeStepName === 'PROGRAMS' && renderProgramsStep()}
              {activeStepName === 'DEMOGRAPHICS' && renderDemographicsStep()}
              {selectedCarePrograms.includes('rpm') && (
                <div
                  hidden={activeProgramStepKey !== 'rpm'}
                  aria-hidden={activeProgramStepKey !== 'rpm'}
                >
                  {renderRPMStep()}
                </div>
              )}
              {activeProgramStepKey && activeProgramStepKey !== 'rpm' && renderProgramStepContent(activeProgramStepKey)}
            </div>
          </div>

          <div className="pif-footer">
            {currentStep === 0 ? (
              editPatientId ? (
                <button
                  type="button"
                  className="pif-events-log-btn"
                  onClick={() => setShowEventLogsModal(true)}
                  title="View Patient Event Logs"
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faList} className="me-2" />
                  Event Logs
                </button>
              ) : (
                <span />
              )
            ) : (
              <button
                type="button"
                className="pif-cancel-btn"
                onClick={() => setCurrentStep((prev) => prev - 1)}
                disabled={isSubmitting}
              >
                Previous
              </button>
            )}

            {currentStep < designCSteps.length - 1 ? (
              <button
                type="button"
                className="pif-save-btn"
                disabled={isSubmitting}
                onClick={async () => {
                  if (isSubmitting) return;
                  const pickStepErrors = (keys) => {
                    const all = validateAllForm();
                    console.log('[AddPatientModal Next Click] validateAllForm result:', all, 'current errors state:', errors);
                    return Object.fromEntries(
                      Object.entries(all).filter(([key]) => keys.includes(key))
                    );
                  };

                  const activeStepName = String(designCSteps[currentStep] || '').toUpperCase();
                  console.log('[AddPatientModal Next Click] activeStepName:', activeStepName, 'currentStep:', currentStep);

                  if (activeStepName === 'PROGRAMS') {
                    const stepErrors = preserveAvailabilityErrors(errors, pickStepErrors(['carePrograms']));
                    console.log('[AddPatientModal Next Click] PROGRAMS stepErrors:', stepErrors);
                    if (!reportValidationErrors(setErrors, stepErrors, null, errors)) return;
                  }
                  if (activeStepName === 'DEMOGRAPHICS') {
                    const stepErrors = preserveAvailabilityErrors(
                      errors,
                      pickStepErrors([...PATIENT_DEMOGRAPHIC_ERROR_KEYS]),
                    );
                    console.log('[AddPatientModal Next Click] DEMOGRAPHICS stepErrors:', stepErrors);
                    if (!reportValidationErrors(setErrors, stepErrors, toast, errors)) return;
                    const availabilityOk = await verifyDemographicAvailability();
                    console.log('[AddPatientModal Next Click] DEMOGRAPHICS availabilityOk:', availabilityOk);
                    if (!availabilityOk) return;
                  }
                  if (activeStepName === 'CCM' || activeStepName === 'PCM') {
                    const stepErrors = preserveAvailabilityErrors(errors, pickStepErrors(['chronicConditions', 'ccmRestrictions', 'ccmConsent', 'ccmConsentSignature']));
                    console.log('[AddPatientModal Next Click] care management stepErrors:', stepErrors);
                    if (!reportValidationErrors(setErrors, stepErrors, toast, errors)) return;
                  }
                  if (activeStepName === 'RPM') {
                    const allErrors = validateAllForm();
                    const rpmStepErrors = Object.fromEntries(
                      Object.entries(allErrors).filter(
                        ([key]) =>
                          key === 'rpmStartDate' ||
                          key === 'monitoringTypes' ||
                          key === 'terms' ||
                          key === 'meterMappings' ||
                          key.startsWith('documentFile') ||
                          isInsuranceErrorKey(key)
                      )
                    );
                    if (!termsAccepted && !rpmStepErrors.terms) {
                      rpmStepErrors.terms = 'You must accept the terms and conditions';
                    }
                    const stepErrors = preserveAvailabilityErrors(errors, rpmStepErrors);
                    console.log('[AddPatientModal Next Click] RPM stepErrors:', stepErrors);
                    if (!reportValidationErrors(setErrors, stepErrors, toast, errors)) {
                      if (stepErrors.terms) {
                        document.getElementById('terms')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                      return;
                    }
                  }

                  setCurrentStep((prev) => prev + 1);
                }}
              >
                Next <ChevronRight size={18} />
              </button>
            ) : (
              <button
                type="button"
                className="pif-save-btn"
                onClick={handleSave}
                disabled={isSubmitting || (patientToEdit && !isChanged)}
              >
                <Save size={18} />
                {isSubmitting ? 'Saving...' : (patientToEdit ? 'Update Patient' : 'Save Patient')}
              </button>
            )}
          </div>
        </div>
      </div>

      {showEventLogsModal && editPatientId && (
        <div ref={logOverlayRef} className="practices-list-modal-overlay" style={{ zIndex: 110000 }}>
          <div
            ref={logContainerRef}
            className="practices-list-modal-container"
            style={{ maxWidth: '900px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="practices-list-modal-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <h5 className="practices-list-modal-title" style={{ margin: 0 }}>
                  {formData.firstName || formData.lastName
                    ? `${formData.firstName} ${formData.lastName} — Event Logs`
                    : 'Patient Event Logs'}
                </h5>
                <button
                  type="button"
                  className="practices-list-modal-close"
                  onClick={() => setShowEventLogsModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="practice-event-logs-tabs" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { id: 'all', label: 'All Logs' },
                  { id: 'Patient', label: 'Demographics' },
                  { id: 'Patient Schedule', label: 'Schedule & Targets' },
                  { id: 'Patient Meter', label: 'Meter Mapping' },
                  { id: 'Patient Document', label: 'Documents' },
                  { id: 'Patient Follow-up', label: 'Follow-up' },
                  { id: 'Patient Care Plan', label: 'Care Plan' },
                  { id: 'Patient Vital', label: 'Vitals' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setPatientLogFilter(tab.id)}
                    className={`practice-log-filter-tab ${patientLogFilter === tab.id ? 'active' : ''}`}
                    style={{
                      padding: '5px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: patientLogFilter === tab.id ? '600' : '500',
                      border: patientLogFilter === tab.id ? '1px solid #03045E' : '1px solid #E2E8F0',
                      backgroundColor: patientLogFilter === tab.id ? '#03045E' : '#F8FAFC',
                      color: patientLogFilter === tab.id ? '#FFFFFF' : '#475569',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {patientLogFilter === 'all' ? (
                <EventLogSection
                  modulePrefix="Patient"
                  patientId={editPatientId}
                  practiceId={effectivePracticeId}
                  patientUsername={formData.username}
                  key="all"
                />
              ) : (
                <EventLogSection
                  moduleId={patientLogFilter}
                  patientId={editPatientId}
                  practiceId={effectivePracticeId}
                  patientUsername={formData.username}
                  key={patientLogFilter}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {programConfirmModal.isOpen && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 105000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#f8f9fa', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                <h5 className="modal-title" style={{ fontWeight: 600, color: '#263651' }}>Confirm Disenrollment</h5>
                <button type="button" className="btn-close" onClick={() => setProgramConfirmModal({ isOpen: false, programKey: null, programObj: null })}></button>
              </div>
              <div className="modal-body p-4">
                <p style={{ fontSize: '15px', marginBottom: '12px' }}>
                  Are you sure you want to remove the patient from the <strong style={{ color: '#d9534f' }}>{programConfirmModal.programObj?.full_name || programConfirmModal.programObj?.short_name || programConfirmModal.programKey.toUpperCase()}</strong> program?
                </p>
                <p className="text-muted small mb-0" style={{ backgroundColor: '#fff4f4', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #d9534f' }}>
                  This action will unenroll the patient from this program and mark it inactive.
                </p>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #f0f0f0' }}>
                <button type="button" className="btn btn-light" onClick={() => setProgramConfirmModal({ isOpen: false, programKey: null, programObj: null })} style={{ fontWeight: 500 }}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmRemoveProgram} style={{ fontWeight: 500, backgroundColor: '#dc3545', borderColor: '#dc3545' }}>Yes, Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showConsentModal && (
        <div className="add-patient-modal-overlay">
          <div className="add-patient-modal-content" id="printable-consent-form">
            <button className="add-patient-close-btn-corner" onClick={() => setShowConsentModal(false)} data-html2canvas-ignore="true">
              <FontAwesomeIcon icon={faTimes} />
            </button>

            {practiceLogoUrl && !consentLogoFailed ? (
              <div className="add-patient-consent-logo-header">
                <img
                  src={practiceLogoUrl}
                  alt=""
                  className="add-patient-consent-practice-logo"
                  referrerPolicy="no-referrer"
                  onError={() => setConsentLogoFailed(true)}
                />
              </div>
            ) : null}

            <div className="add-patient-consent-title-section">
              <span className="add-patient-doc-title">PATIENT CONSENT FORM</span>
              <span className="add-patient-doc-subtitle">Remote Physiological Monitoring</span>
            </div>

            <div className="add-patient-consent-scroll-body">
              <div className="add-patient-doc-section">
                <h5 className="add-patient-doc-section-title">Patient Details:</h5>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Name :</label>
                    <input type="text" name="name" className="add-patient-consent-input-line" value={consentForm.name} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>DOB :</label>
                    <AppDateInput
                      name="dob"
                      className="add-patient-consent-input-line"
                      value={consentForm.dob}
                      onChange={(v) => handleConsentChange({ target: { name: 'dob', value: v } })}
                      max={getMaxDateOfBirthForInput()}
                    />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Tel :</label>
                    <input type="text" name="tel" className="add-patient-consent-input-line" value={consentForm.tel} onChange={handleConsentChange} />
                  </div>
                </div>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Address :</label>
                    <input type="text" name="address" className="add-patient-consent-input-line" value={consentForm.address} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Email <span className="add-patient-required">*</span> :</label>
                    <input type="email" name="email" className="add-patient-consent-input-line" value={consentForm.email} onChange={handleConsentChange} />
                  </div>
                </div>
              </div>

              <div className="add-patient-doc-section">
                <h5 className="add-patient-doc-section-title uppercase">MEDICAL DEVICES SUPPLIED TO PATIENT</h5>
                <p className="add-patient-doc-text-intro">
                  By signing the Patient Consent Form, I hereby consent to {consentIntroParty} providing Remote Physiological Monitoring (RPM) services to me and agree to the following.
                </p>
                <ul className="add-patient-doc-list">
                  <li>The medical Devices provided to me are the property of the Provider. I will not tamper with the Medical Device and I understand that I am responsible for any cost and expenses related to the misuse of the Medical Device.</li>
                  <li>I will be the only one to use the medical device as instructed by the provider and I will not use the device for reasons other than health monitoring.</li>
                  <li>The medical device is to be used solely in connection with the RPM Services and associated RPM program provided by the provider and not for any other purpose.</li>
                  <li>THE RPM PROGRAM IS NOT A REPLACEMENT FOR EMERGENCY SERVICE. CALL 9-1-1 FOR IMMEDIATE MEDICAL EMERGENCIES AND DO NOT USE THE MEDICAL DEVICE IN REPLACEMENT OF THE 9-1-1 SERVICE.</li>
                  <li>The length of time I am on the RPM program will be determined by the provider at its sole discretion and I may be removed from the RPM program at any time.</li>
                  <li>My data and medical information obtained from my participation in the RPM program may /may not be considered part of my patient record, but may be shared with other health care professionals to enhance my care and I authorize such professional disclosure. My data and information will be securely transmitted data and reviewed at the clinician's discretion.</li>
                  <li>I will comply with the RPM program clinical expectations and if I do not, I may be removed from the RPM program and I will return the medical device.</li>
                  <li>I have the rights to withdraw my consent to participate in the RPM program and stop receiving RPM Service at any time.</li>
                </ul>

                <p className="add-patient-doc-text-intro" style={{ marginTop: '15px' }}><strong>Provider's obligation:</strong> When providing the RPM Services, the Provider must</p>
                <ul className="add-patient-doc-list">
                  <li>Clearly explain the detail of the RPM Service to be provided to you and that are applicable to your conditions.</li>
                  <li>Provide you with a written copy of your care plan.</li>
                  <li>Provide you with a written confirmation of your revocation from receiving the RPM Services and participating in the RPM if you revoke your consent.</li>
                </ul>
              </div>

              <div className="add-patient-doc-section">
                <h5 className="add-patient-doc-section-title">Patient Consent:</h5>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Patient Signature :</label>
                    <input type="text" name="patientSignature" className="add-patient-consent-input-line" value={consentForm.patientSignature} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Date :</label>
                    <AppDateInput
                      name="patientDate"
                      className="add-patient-consent-input-line"
                      value={consentForm.patientDate || ''}
                      onChange={(v) => handleConsentChange({ target: { name: 'patientDate', value: v } })}
                      min={practiceCreatedAtStr || undefined}
                      title={
                        practiceCreatedAtStr
                          ? `Select a date on or after ${practiceCreatedAtStr}`
                          : 'Select a date'
                      }
                    />
                  </div>
                </div>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>Clinician Signature :</label>
                    <input type="text" name="clinicianSignature" className="add-patient-consent-input-line" value={consentForm.clinicianSignature} onChange={handleConsentChange} />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>Date :</label>
                    <AppDateInput
                      name="clinicianDate"
                      className="add-patient-consent-input-line"
                      value={consentForm.clinicianDate || ''}
                      onChange={(v) => handleConsentChange({ target: { name: 'clinicianDate', value: v } })}
                      min={practiceCreatedAtStr || undefined}
                      title={
                        practiceCreatedAtStr
                          ? `Select a date on or after ${practiceCreatedAtStr}`
                          : 'Select a date'
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="add-patient-doc-section">
                <p className="add-patient-doc-text-small">
                  In the event that the patient is unable to give consent due to mental or other capacity issues, the relative, or caregiver who has Lasting/Durable Power of Attorney for the health and welfare responsibly, may give consent if deemed to be in the best interest of the patient. In these instances, the caregiver/relative should sign below.
                </p>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field full-width">
                    <label>CareGiver/relative name :</label>
                    <input type="text" name="caregiverName" className="add-patient-consent-input-line" value={consentForm.caregiverName} onChange={handleConsentChange} />
                  </div>
                </div>
                <div className="add-patient-doc-row">
                  <div className="add-patient-doc-field flex-2">
                    <label>CareGiver/relative signature :</label>
                    <input
                      type="text"
                      name="caregiverSignature"
                      className="add-patient-consent-input-line"
                      value={consentForm.caregiverSignature}
                      onChange={handleConsentChange}
                      required={!patientToEdit}
                    />
                  </div>
                  <div className="add-patient-doc-field flex-1">
                    <label>
                      Date <span className="text-red-500">*</span> :
                    </label>
                    <AppDateInput
                      name="caregiverDate"
                      className="add-patient-consent-input-line"
                      value={consentForm.caregiverDate || ''}
                      onChange={(v) => handleConsentChange({ target: { name: 'caregiverDate', value: v } })}
                      required={!patientToEdit}
                      min={practiceCreatedAtStr || undefined}
                      title={
                        practiceCreatedAtStr
                          ? `Select a date on or after ${practiceCreatedAtStr}`
                          : 'Select a date'
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="add-patient-doc-footer-buttons" data-html2canvas-ignore="true">
              <button
                type="button"
                className="add-patient-btn-doc-print"
                onClick={handleDownloadPDF}
                disabled={consentPdfDownloading}
              >
                {consentPdfDownloading ? 'Downloading…' : 'Download'}
              </button>
              <button
                className="add-patient-btn-doc-agree"
                onClick={handleAgreeToConsent}
                disabled={!patientToEdit && !consentForm.caregiverDate}
                title={
                  !patientToEdit && !consentForm.caregiverDate
                    ? 'Enter caregiver/relative signature date before agreeing.'
                    : ''
                }
              >
                Agree
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Status Device Removal Confirmation Modal */}
      <PatientStatusDeviceRemovalModal
        show={statusDeviceRemovalModal.show}
        patientName={patientToEdit ? ([formData.firstName, formData.lastName].filter(Boolean).join(' ') || 'this patient') : 'this patient'}
        targetStatus={statusDeviceRemovalModal.targetStatus}
        devices={meterMappings}
        onConfirm={handleConfirmStatusDeviceRemoval}
        onCancel={handleCancelStatusDeviceRemoval}
      />
      <BpEnrollmentSettingsModal
        open={vitalModal != null}
        kind={vitalModal || 'bp'}
        practiceId={practiceId}
        initialMode={vitalModal ? vitalModes[vitalModal] : null}
        onCancel={handleVitalSettingsCancel}
        onConfirm={handleVitalSettingsConfirm}
      />
    </>
  );

  function renderProgramsStep() {
    return (
      <div className="pif-section">
        <h3 className="pif-section-title">Enroll in Care Programs <span className="text-red-500">*</span></h3>
        {(practiceHasPcm || practiceHasCcm) ? (
          <div className="ccm-info-banner" style={{ marginBottom: '14px' }}>
            <AlertCircle size={16} style={{ color: '#d97706', flexShrink: 0 }} />
            <span>
              {practiceHasPcm && practiceHasCcm
                ? 'PCM is for one high-risk chronic condition (30+ minutes/month, CPT 99424–99427). CCM is for two or more. Choose PCM or CCM yourself — adding conditions will not switch the program. A patient cannot be in both.'
                : practiceHasPcm
                  ? 'PCM is for a single high-risk chronic condition expected to last at least 3 months. Select one principal disease only. Monthly PCM cannot be billed for less than 30 minutes (CPT 99424–99427).'
                  : 'CCM is for patients with two or more chronic conditions.'}
            </span>
          </div>
        ) : null}
        <div className="pif-program-vertical-stack">
          {availableCarePrograms.length === 0 ? (
            <p className="text-muted">No care programs are enrolled for this practice.</p>
          ) : availableCarePrograms.map((prog) => {
            const Icon = getLucideProgramIcon(prog);
            const progKey = normalizeProgramKey(prog.short_name || prog.id);
            const isChecked = selectedCarePrograms.includes(progKey);

            return (
              <div
                key={prog.id}
                className={`pif-prog-card-modern ${isChecked ? 'selected' : ''}`}
                onClick={() => handleCareProgramToggle(progKey, prog)}
              >
                <div className="pif-prog-card-content">
                  <Icon size={18} style={{ color: prog.badge_color || '#3182ce' }} />
                  <span className="pif-prog-name">{prog.full_name || prog.label || prog.name}</span>
                </div>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => { }} // Click handled by wrapper div
                  className="pif-prog-checkbox"
                />
              </div>
            );
          })}
        </div>
        <FormFieldError message={errors.carePrograms} />
      </div>
    );
  }

  function renderDemographicsStep() {
    return (
      <div className="pif-aligned-grid">

        {/* ── SECTION LABEL: Personal Info ── */}
        <div className="pif-demo-section-label">Personal Information</div>

        {/* Row 1: Title + First Name | Middle Initial + Last Name */}
        <div className="pif-form-row-balanced">
          <div className="pif-form-row" style={{ flex: 1 }}>
            <div className="pif-form-group pif-title-group">
              <label>Title</label>
              <select name="title" className={`pif-input ${errors.title ? 'is-invalid' : ''}`} value={formData.title} onChange={handleInputChange}>
                <option value="">-</option>
                <option value="Dr.">Dr.</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
              </select>
              {errors.title && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.title}</span>}
            </div>
            <div className="pif-form-group pif-flex-1">
              <label>First Name <span className="text-red-500">*</span></label>
              <input type="text" name="firstName" className={`pif-input ${errors.firstName ? 'is-invalid' : ''}`} value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" />
              {errors.firstName && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.firstName}</span>}
            </div>
          </div>
          <div className="pif-form-row" style={{ flex: 1 }}>
            <div className="pif-form-group pif-mi-group">
              <label>Middle Name</label>
              <input type="text" name="middleInitial" className="pif-input" value={formData.middleInitial} onChange={handleInputChange} placeholder="e.g., Marie" maxLength={20} />
            </div>
            <div className="pif-form-group pif-flex-1">
              <label>Last Name <span className="text-red-500">*</span></label>
              <input type="text" name="lastName" className={`pif-input ${errors.lastName ? 'is-invalid' : ''}`} value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" />
              {errors.lastName && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.lastName}</span>}
            </div>
          </div>
        </div>

        {/* Row 2: Gender | Date of Birth */}
        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>Gender <span className="text-red-500">*</span></label>
            <select name="gender" className="pif-input" value={formData.gender} onChange={handleInputChange}>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div className="pif-form-group">
            <label>Date of Birth <span className="text-red-500">*</span></label>
            <AppDateInput name="dateOfBirth" className={`pif-input ${errors.dateOfBirth ? 'is-invalid' : ''}`} max={new Date().toISOString().split('T')[0]} value={formData.dateOfBirth} onChange={(v) => handleInputChange({ target: { name: 'dateOfBirth', value: v } })} />
            {errors.dateOfBirth && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.dateOfBirth}</span>}
          </div>
        </div>

        {/* Row 3: SSN */}
        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>SSN</label>
            <input type="text" name="ssn" className={`pif-input ${errors.ssn ? 'is-invalid' : ''}`} placeholder="000-00-0000" value={formData.ssn} onChange={handleInputChange} onBlur={handleSsnBlur} />
            {errors.ssn && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.ssn}</span>}
          </div>
        </div>

        {/* Row 4: Home Phone | Cell Phone */}
        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>Home Phone</label>
            <PhoneNumberInput name="homePhone" className={`pif-input ${errors.homePhone ? 'is-invalid' : ''}`} placeholder="(000) 000-0000" value={formData.homePhone} onChange={handleInputChange} />
            {errors.homePhone && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.homePhone}</span>}
          </div>
          <div className="pif-form-group">
            <label>Cell Phone <span className="text-red-500">*</span></label>
            <PhoneNumberInput name="cellPhone" className={`pif-input ${errors.cellPhone ? 'is-invalid' : ''}`} placeholder="(000) 000-0000" value={formData.cellPhone} onChange={handleInputChange} />
            {errors.cellPhone && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.cellPhone}</span>}
          </div>
        </div>

        <div className="pif-form-row-balanced">
          <div className="pif-form-group" style={{ flex: 0.5 }}>
            <label>Fax</label>
            <PhoneNumberInput name="fax" className={`pif-input ${errors.fax ? 'is-invalid' : ''}`} placeholder="(000) 000-0000" value={formData.fax} onChange={handleInputChange} />
            {errors.fax && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.fax}</span>}
          </div>
        </div>

        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Emergency Contact</div>
        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>First Name</label>
            <input type="text" name="emergencyContactFirstName" className="pif-input" placeholder="e.g., Jane" maxLength={80} value={formData.emergencyContactFirstName || ''} onChange={handleInputChange} />
          </div>
          <div className="pif-form-group">
            <label>Last Name</label>
            <input type="text" name="emergencyContactLastName" className="pif-input" placeholder="e.g., Doe" maxLength={80} value={formData.emergencyContactLastName || ''} onChange={handleInputChange} />
          </div>
        </div>
        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>Relationship to Patient</label>
            <select name="emergencyContactRelation" className="pif-input" value={formData.emergencyContactRelation || ''} onChange={handleInputChange}>
              <option value="">Select Relationship</option>
              {EMERGENCY_CONTACT_RELATIONS.map((relation) => (
                <option key={relation} value={relation}>{relation}</option>
              ))}
            </select>
          </div>
          <div className="pif-form-group">
            <label>Phone</label>
            <PhoneNumberInput name="emergencyContactPhone" className={`pif-input ${errors.emergencyContactPhone ? 'is-invalid' : ''}`} placeholder="(000) 000-0000" value={formData.emergencyContactPhone || ''} onChange={handleInputChange} />
            {errors.emergencyContactPhone && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.emergencyContactPhone}</span>}
          </div>
        </div>
        <div className="pif-form-row-balanced">
          <div className="pif-form-group" style={{ flex: 1 }}>
            <label>Address</label>
            <input type="text" name="emergencyContactAddress" className="pif-input" placeholder="e.g., 123 Main St, New York, NY 10001" maxLength={250} value={formData.emergencyContactAddress || ''} onChange={handleInputChange} />
          </div>
        </div>

        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Account Credentials</div>

        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>
              Username <span className="text-red-500">*</span>
              {checking.username && <span style={{ fontSize: '11px', color: '#6366f1', marginLeft: '6px' }}>(checking...)</span>}
            </label>
            <input type="text" name="username" className={`pif-input ${errors.username ? 'is-invalid' : ''}`} value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={handleUsernameBlur} placeholder="e.g., john.doe" maxLength={USERNAME_MAX_LENGTH} />
            {errors.username && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.username}</span>}
          </div>
          <div className="pif-form-group">
            <label>
              Email <span className="text-red-500">*</span>
              {checking.email && <span style={{ fontSize: '11px', color: '#6366f1', marginLeft: '6px' }}>(checking...)</span>}
            </label>
            <input type="email" name="email" className={`pif-input ${errors.email ? 'is-invalid' : ''}`} placeholder="e.g., patient@example.com" value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} onBlur={handleEmailBlur} />
            {errors.email && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.email}</span>}
          </div>
        </div>

        {/* Password | Confirm Password */}
        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>Password {!patientToEdit && <span className="text-red-500">*</span>}</label>
            <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input type={showPassword ? "text" : "password"} name="password" className={`pif-input ${errors.password ? 'is-invalid' : ''}`} value={formData.password} onChange={handleInputChange} placeholder={patientToEdit ? "Leave blank to keep current" : "Enter at least 8 characters"} onFocus={() => setShowPasswordCriteria(true)} onBlur={() => setShowPasswordCriteria(false)} style={{ paddingRight: '40px' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
              {showPasswordCriteria && (
                <div className="ap-password-criteria-block">
                  <div className={`ap-criteria-item ${formData.password.length >= 8 ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={formData.password.length >= 8 ? faCheckCircle : faCircle} className="ap-criteria-icon" /> Minimum 8 characters
                  </div>
                  <div className={`ap-criteria-item ${/[A-Z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[A-Z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Uppercase letter
                  </div>
                  <div className={`ap-criteria-item ${/[a-z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[a-z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Lowercase letter
                  </div>
                  <div className={`ap-criteria-item ${/[0-9]/.test(formData.password) ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[0-9]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Number
                  </div>
                  <div className={`ap-criteria-item ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Special character
                  </div>
                </div>
              )}
            </div>
            {errors.password && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.password}</span>}
          </div>
          <div className="pif-form-group">
            <label>Confirm Password {!patientToEdit && <span className="text-red-500">*</span>}</label>
            <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" className={`pif-input ${errors.confirmPassword ? 'is-invalid' : ''}`} value={formData.confirmPassword} onChange={handleInputChange} placeholder={patientToEdit ? "Leave blank to keep current" : "Enter at least 8 characters"} style={{ paddingRight: '40px' }} />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            {errors.confirmPassword && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.confirmPassword}</span>}
          </div>
        </div>

        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Address &amp; Location</div>

        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
              <label style={{ marginBottom: 0 }}>Address Line 1 <span className="text-red-500">*</span></label>
              <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine1?.length || 0}/250</span>
            </div>
            <input type="text" name="addressLine1" maxLength={250} className={`pif-input ${errors.addressLine1 ? "is-invalid" : ""}`} value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" />
            <FormFieldError message={errors.addressLine1} />
          </div>
          <div className="pif-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
              <label style={{ marginBottom: 0 }}>Address Line 2</label>
              <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine2?.length || 0}/250</span>
            </div>
            <input type="text" name="addressLine2" maxLength={250} className={`pif-input ${errors.addressLine2 ? "is-invalid" : ""}`} value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
          </div>
        </div>

        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>City <span className="text-red-500">*</span></label>
            <input type="text" name="city" className={`pif-input ${errors.city ? 'is-invalid' : ''}`} value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" />
            <FormFieldError message={errors.city} />
          </div>
          <div className="pif-form-group">
            <label>State <span className="text-red-500">*</span></label>
            <select name="state" className={`pif-input ${errors.state ? 'is-invalid' : ''}`} value={formData.state} onChange={handleInputChange}>
              <option value="">Select State</option>
              {US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
            <FormFieldError message={errors.state} />
          </div>
        </div>

        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>Zip Code <span className="text-red-500">*</span></label>
            <input type="text" name="zipCode" className={`pif-input ${errors.zipCode ? 'is-invalid' : ''}`} value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" />
            <FormFieldError message={errors.zipCode} />
          </div>
          <div className="pif-form-group">
            <label>Time Zone <span className="text-red-500">*</span></label>
            <select name="timeZone" className={`pif-input ${errors.timeZone ? 'is-invalid' : ''}`} value={formData.timeZone} onChange={handleInputChange} required>
              <option value="">Select Time Zone</option>
              {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
            {errors.timeZone && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.timeZone}</span>}
          </div>
        </div>

        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Care Team &amp; Settings</div>

        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>Status <span className="text-red-500">*</span></label>
            <select name="status" className="pif-input" value={formData.status} onChange={handleInputChange}>
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Locked">Locked</option>
            </select>
          </div>
          <div className="pif-form-group">
            <label>Two Way Authentication <span className="text-red-500">*</span></label>
            <select name="twoWayAuth" className="pif-input" value={formData.twoWayAuth} onChange={handleInputChange}>
              <option value="Enabled">Enabled</option>
              <option value="Disabled">Disabled</option>
            </select>
          </div>
        </div>

        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <label>Mobile App Access</label>
            <select name="mobileAppAccess" className="pif-input" value={formData.mobileAppAccess || 'Disabled'} onChange={handleInputChange}>
              <option value="Disabled">Off</option>
              <option value="Enabled">On</option>
            </select>
          </div>
        </div>

        {/* Provider | System Caregiver (system caregiver only for eVitals monitoring practices) */}
        <div className="pif-form-row-balanced">
          <div className="pif-form-group">
            <PatientAscStyleSinglePicker
              label="Provider"
              name="provider"
              required
              value={formData.provider}
              options={activePracticeProviders}
              error={errors.provider}
              placeholder={activePracticeProviders.length === 0 ? 'No providers available' : 'Select provider'}
              emptyMessage="No providers available"
              onPick={pickProviderField}
            />
          </div>
          {requiresSystemCaregiver && (
            <div className="pif-form-group">
              <PatientAscStyleMultiPicker
                label="System Caregiver"
                required
                selectedIds={formData.systemCaregiverIds}
                options={systemCaregiverPickerOptions}
                onAddId={addSystemCaregiverId}
                onRemoveId={removeSystemCaregiverId}
                error={errors.systemCaregiver}
                placeholder="Select system caregivers"
                emptyMessage="No system caregivers available"
              />
            </div>
          )}
        </div>

        {/* Practice Caregiver | Upload Picture */}
        <div className="pif-form-row-balanced" style={{ alignItems: 'flex-start' }}>
          <div className="pif-form-group" style={{ marginBottom: '0' }}>
            <PatientAscStyleMultiPicker
              label="Practice Caregiver"
              required
              selectedIds={formData.practiceCaregiverIds}
              options={practiceCaregiverPickerOptions}
              onAddId={addPracticeCaregiverId}
              onRemoveId={removePracticeCaregiverId}
              error={errors.practiceCaregiver}
              placeholder="Select practice caregivers"
              emptyMessage="No practice caregivers available"
            />
          </div>
          <div className="pif-form-group" style={{ marginBottom: '0' }}>
            <label>Upload Picture</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                className={`pif-input ${errors.profilePicture ? 'is-invalid' : ''}`}
                placeholder="No file chosen (max 1 MB)"
                readOnly
                value={pictureFile ? pictureFile.name : (existingProfilePic ? existingProfilePic.split('/').pop() : '')}
                onClick={() => pictureInputRef.current && pictureInputRef.current.click()}
                style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                className="pif-upload-btn"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={() => pictureInputRef.current && pictureInputRef.current.click()}
              >
                <Upload size={16} /> Upload
              </button>
              <input
                ref={pictureInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handlePictureChange}
              />
              {/* Inline preview — same row */}
              {(pictureFile || existingProfilePicUrl) && (
                <>
                  <img
                    src={pictureFile ? URL.createObjectURL(pictureFile) : existingProfilePicUrl}
                    alt="Profile Preview"
                    style={{
                      height: '38px', width: '38px', objectFit: 'cover',
                      borderRadius: '50%', border: '2px solid #e0e0e0', flexShrink: 0
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    style={{
                      background: 'none', border: 'none', color: '#d32f2f',
                      cursor: 'pointer', fontSize: '11px', fontWeight: 700,
                      flexShrink: 0, padding: '0'
                    }}
                    onClick={handleRemovePicture}
                  >
                    ✕
                  </button>
                </>
              )}
            </div>
            <FormFieldError message={errors.profilePicture} />
          </div>
        </div>

      </div>
    );
  }

  function renderRPMStep() {
    return (
      <div className="pif-section">
        <div className="pif-rpm-setup-block !mb-4">
          <div className={`pif-rpm-setup-card ${errors.rpmStartDate || errors.monitoringTypes ? 'has-error' : ''}`}>
            <div className="pif-rpm-setup-card-header">
              <h4 className="pif-rpm-setup-card-title">
                RPM monitoring setup <span className="pif-rpm-required">*</span>
              </h4>
              <div className={`pif-rpm-start-field ${errors.rpmStartDate ? 'is-invalid' : ''}`}>
                <AppDateInput
                  id="rpm-start-date-input"
                  name="rpmStartDate"
                  showCalendarIcon
                  readOnly
                  required
                  aria-label="RPM start date"
                  aria-required="true"
                  aria-invalid={Boolean(errors.rpmStartDate)}
                  wrapperClassName="pif-rpm-start-date-wrap"
                  className="pif-input pif-rpm-start-date-input"
                  max={new Date().toISOString().split('T')[0]}
                  value={formData.rpmStartDate}
                  onChange={(v) => handleInputChange({ target: { name: 'rpmStartDate', value: v } })}
                  placeholder="mm/dd/yyyy"
                />
              </div>
            </div>

            <div className="pif-rpm-setup-card-divider" aria-hidden="true" />

            <div className="pif-rpm-monitoring-section">
              <p className="pif-rpm-monitoring-section-label">
                Type of monitoring <span className="pif-rpm-required">*</span>
              </p>
              <div className={`pif-monitoring-rows ${errors.monitoringTypes ? 'is-invalid' : ''}`}>
                {[
                  ...availableMonitoringTypes,
                  ...selectedMonitoring.filter((t) => !availableMonitoringTypes.includes(t))
                ].map((type) => {
                  const isSelected = selectedMonitoring.includes(type);
                  const viewOnly = isSelected && !availableMonitoringTypes.includes(type);
                  const options = getIcdOptionsForMonitoringLabel(type, icdCodesByMeasureType);
                  const currentIcd = monitoringIcdValues[type] || '';
                  const inputId = `monitoring-type-${type.replace(/\s+/g, '-').toLowerCase()}`;
                  const displayName =
                    type === 'Blood Pressure'
                      ? 'Blood pressure'
                      : type === 'Blood Glucose'
                        ? 'Blood glucose'
                        : 'Weight';
                  return (
                    <div key={type} className={`pif-monitoring-row ${isSelected ? 'is-selected' : ''}`}>
                      <label className="pif-monitoring-toggle" htmlFor={inputId}>
                        <input
                          id={inputId}
                          type="checkbox"
                          name="monitoringTypes"
                          value={type}
                          checked={isSelected}
                          disabled={viewOnly}
                          onChange={() => handleMonitoringToggle(type)}
                        />
                        <span className="pif-monitoring-name">
                          {displayName}{viewOnly ? ' (view only)' : ''}
                          {(() => {
                            const kind = monitoringVitalKind(type);
                            const mode = kind ? vitalModes[kind] : null;
                            if (!kind || !isSelected || !mode) return null;
                            return (
                            <button
                              type="button"
                              className="pif-monitoring-bp-note"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setVitalModal(kind);
                              }}
                            >
                              {mode === 'custom' ? 'Using custom settings' : 'Using practice settings'}
                            </button>
                            );
                          })()}
                        </span>
                      </label>
                      <div className="pif-monitoring-icd-field">
                        <span className="pif-monitoring-icd-label">ICD code</span>
                        <IcdCodeSearchField
                          value={currentIcd}
                          localOptions={options}
                          disabled={!isSelected || viewOnly}
                          onChange={(code) => handleMonitoringIcdChange(type, code)}
                          ariaLabel={`ICD code for ${displayName}`}
                          inputClassName="pif-input pif-monitoring-icd-select"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <FormFieldError message={errors.rpmStartDate} />
            <FormFieldError message={errors.monitoringTypes} />
          </div>
        </div>

        <div className="pif-section">
          <h3 className="pif-section-title">Meter Mapping</h3>
          <div className="pif-dynamic-list">
            {meterMappings.map((meter, idx) => (
              <div key={idx} className="pif-dynamic-row pif-form-row-balanced pif-meter-map-row" style={{ alignItems: 'flex-end' }}>
                <div className="pif-form-group pif-flex-1">
                  <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Model Number</label>
                  <select className="pif-input" value={meter.modelNumber || meter.model_number || ''} onChange={(e) => handleMeterChange(idx, 'modelNumber', e.target.value)}>
                    <option value="">Select Model</option>
                    {products.map(p => <option key={p.id} value={p.model_number}>{formatMeterProductOptionLabel(p)}</option>)}
                  </select>
                </div>
                <div className="pif-form-group pif-flex-1">
                  <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Extension</label>
                  <select className="pif-input" value={METER_EXTENSION_OPTIONS.some((o) => o.value === String(meter.extension || '')) ? String(meter.extension) : ''} onChange={(e) => handleMeterChange(idx, 'extension', e.target.value)}>
                    <option value="">Select Extension</option>
                    {METER_EXTENSION_OPTIONS.map((ext) => (
                      <option key={ext.value} value={ext.value}>{ext.label}</option>
                    ))}
                  </select>
                </div>
                <div className="pif-form-group pif-flex-1 pif-meter-serial">
                  <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Serial Number</label>
                  <input type="text" className="pif-input" maxLength={16} value={meter.serialNumber || meter.serial_number || ''} onChange={(e) => handleMeterChange(idx, 'serialNumber', e.target.value)} placeholder="16-character alphanumeric" />
                </div>
                <div className="pif-form-group pif-flex-1">
                  <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Gateway Serial Number</label>
                  <input type="text" className="pif-input" value={meter.gatewaySerialNumber || meter.gateway_serial_number || ''} onChange={(e) => handleMeterChange(idx, 'gatewaySerialNumber', e.target.value)} />
                </div>
                <div className="pif-form-group" style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                  <label>&nbsp;</label>
                  {idx === meterMappings.length - 1 ? (
                    <button type="button" onClick={handleAddMeter} className="p-2 text-gray-700 hover:bg-gray-100 rounded mb-1" style={{ marginBottom: '2px' }} title="Add Device">
                      <Plus size={18} />
                    </button>
                  ) : (
                    <button type="button" onClick={() => handleRemoveMeter(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded mb-1" style={{ marginBottom: '2px' }} title="Remove Device">
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <FormFieldError message={errors.meterMappings} />
        </div>

        <div className="pif-section">
          <h3 className="pif-section-title">Insurance</h3>
          <div className="pif-dynamic-list">
            {(['primary', 'secondary']).map((tier) => {
              const ins = insuranceData[tier];
              const tierLabel = tier === 'primary' ? 'Primary' : 'Secondary';
              return (
                <div key={tier} className="pif-dynamic-row">
                  <div className="pif-form-row-balanced pif-mb-2" style={{ alignItems: 'flex-start' }}>
                    <div className="pif-form-group" style={{ flex: '0 0 90px', paddingTop: '32px' }}>
                      <label className="pif-tier-label" style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>{tierLabel}</label>
                    </div>
                    <div className="pif-form-group pif-flex-1">
                      <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', minHeight: '18px' }}>Insurance Name</label>
                      <input
                        type="text"
                        className={`pif-input ${errors[`${tier}InsuranceName`] ? 'pif-input-error' : ''}`}
                        value={ins.insuranceName || ins.insurance_name || ''}
                        onChange={(e) => handleInsuranceChange(tier, 'insuranceName', e.target.value)}
                        onBlur={() => handleInsuranceBlur(tier, 'insuranceName')}
                        autoComplete="off"
                      />
                      <FormFieldError message={errors[`${tier}InsuranceName`]} />
                    </div>
                    <div className="pif-form-group pif-flex-1">
                      <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', minHeight: '18px' }}>Subscriber Id</label>
                      <input
                        type="text"
                        className={`pif-input ${errors[`${tier}SubscriberId`] ? 'pif-input-error' : ''}`}
                        value={ins.subscriberId || ins.subscriber_id || ''}
                        onChange={(e) => handleInsuranceChange(tier, 'subscriberId', e.target.value)}
                        onBlur={() => handleInsuranceBlur(tier, 'subscriberId')}
                        maxLength={20}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pasted = e.clipboardData?.getData('text') || '';
                          handleInsuranceChange(tier, 'subscriberId', pasted);
                        }}
                        autoComplete="off"
                      />
                      <FormFieldError message={errors[`${tier}SubscriberId`]} />
                    </div>
                    <div className="pif-form-group pif-flex-1">
                      <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', minHeight: '18px' }}>Insured's Name</label>
                      <input
                        type="text"
                        className={`pif-input ${errors[`${tier}InsuredName`] ? 'pif-input-error' : ''}`}
                        value={ins.insuredName || ins.insured_name || ''}
                        onChange={(e) => handleInsuranceChange(tier, 'insuredName', e.target.value)}
                        onBlur={() => handleInsuranceBlur(tier, 'insuredName')}
                        autoComplete="off"
                      />
                      <FormFieldError message={errors[`${tier}InsuredName`]} />
                    </div>
                    <div className="pif-form-group pif-flex-1">
                      <label style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', minHeight: '18px' }}>Group No.</label>
                      <input
                        type="text"
                        className={`pif-input ${errors[`${tier}GroupNo`] ? 'pif-input-error' : ''}`}
                        value={ins.groupNo || ins.group_no || ins.group_number || ''}
                        onChange={(e) => handleInsuranceChange(tier, 'groupNo', e.target.value)}
                        onBlur={() => handleInsuranceBlur(tier, 'groupNo')}
                        maxLength={15}
                        autoComplete="off"
                      />
                      <FormFieldError message={errors[`${tier}GroupNo`]} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="pif-section">
          <PatientDocumentsSection
            ref={documentsSectionRef}
            practiceId={practiceId}
            patientId={editingPatientId}
            isEditMode={Boolean(patientToEdit)}
            errors={errors}
            setErrors={setErrors}
            showSavedTable={Boolean(patientToEdit)}
            variant="modal"
            title="Upload documents"
            onPendingDocumentsChange={setPendingDocumentCount}
          />
        </div>

        <div className="pif-section">
          <div className="add-patient-terms-container">
            <input
              type="checkbox"
              id="terms"
              className="add-patient-terms-checkbox"
              checked={termsAccepted}
              onChange={() => { }}
              onClick={handleTermsClick}
              readOnly
            />
            <label htmlFor="terms" className="add-patient-terms-label-text" onClick={handleTermsClick}>
              Terms & Conditions <span className="add-patient-required">*</span>
            </label>
          </div>
          <FormFieldError message={errors.terms} />
        </div>
      </div>
    );
  }

  function renderPatientSignatureField() {
    return (
      <div className="pif-form-group ccm-signature-group">
        <label>
          Patient Signature <span className="text-red-500">*</span>
        </label>
        <p className="ccm-signature-help">
          Use Draw Signature to sign with your mouse, or Upload Signature for a JPG/PNG image file.
        </p>
        <SignaturePad
          value={ccmConsentSignature}
          signatureSource={ccmConsentSignatureSource}
          hasError={Boolean(errors.ccmConsentSignature)}
          onSignatureSourceChange={setCcmConsentSignatureSource}
          onChange={(dataUrl, source) => {
            setCcmConsentSignature(dataUrl);
            if (source) {
              setCcmConsentSignatureSource(source);
            }
            if (dataUrl) {
              setErrors((prev) => {
                if (!prev.ccmConsentSignature) return prev;
                const next = { ...prev };
                delete next.ccmConsentSignature;
                return next;
              });
            }
          }}
        />
        <FormFieldError message={errors.ccmConsentSignature} />
      </div>
    );
  }

  function renderCCMStep() {
    return (
      <div className="pif-ccm-combined-layout">
        <div className="pif-section">
          <PatientChronicConditionsSection
            conditions={selectedChronicConditions}
            onChange={(nextConditions) => {
              setSelectedChronicConditions(nextConditions);
              syncCareManagementFromConditions(nextConditions);
            }}
            minRequired={2}
            hasError={Boolean(errors.chronicConditions)}
            errorMessage={errors.chronicConditions}
            title="Chronic Disease Selection (Min. 2)"
            description="Select a disease to open the condition form and enter ICD code, severity, date of diagnosis, status, and billing role — same as care plan conditions."
          />
        </div>

        <div className="pif-ccm-divider"></div>

        {/* ── Section 2: Patient Consent ── */}
        <div className="ccm-section-block">
          <div className="ccm-section-header">
            <FileText size={15} className="ccm-section-icon" />
            <span className="ccm-section-heading">Patient Consent</span>
          </div>

          <div className="ccm-consent-tabs">
            <button
              type="button"
              className={`ccm-consent-tab ${ccmConsentType === 'verbal' ? 'active' : ''}`}
              onClick={() => {
                setCcmConsentType('verbal');
                setErrors((prev) => {
                  if (!prev.ccmConsentSignature) return prev;
                  const next = { ...prev };
                  delete next.ccmConsentSignature;
                  return next;
                });
              }}
            >Verbal Consent</button>
            <button
              type="button"
              className={`ccm-consent-tab ${ccmConsentType === 'written' ? 'active' : ''}`}
              onClick={() => {
                setCcmConsentType('written');
              }}
            >Written Consent</button>
          </div>

          {ccmConsentType === 'verbal' ? (
            <div className="ccm-verbal-body">
              <div className="ccm-checklist">
                {[
                  'Did you explain the CCM services, including availability of 24/7 access to care?',
                  'Did you inform the patient that only one practitioner can furnish and be paid for CCM services during a calendar month?',
                  'Did you inform the patient of their right to stop CCM services at any time?'
                ].map((q, i) => (
                  <label key={i} className={`ccm-check-row ${ccmVerbalAnswers[i] ? 'checked' : ''}`} style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      className="ccm-check-input"
                      checked={ccmVerbalAnswers[i]}
                      onChange={() => {
                        const newAnswers = [...ccmVerbalAnswers];
                        newAnswers[i] = !newAnswers[i];
                        setCcmVerbalAnswers(newAnswers);
                      }}
                    />
                    <span className={`ccm-check-custom ${ccmVerbalAnswers[i] ? 'checked' : ''}`}>
                      {ccmVerbalAnswers[i] && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className="ccm-check-label">{q}</span>
                  </label>
                ))}
              </div>
              <div className="pif-form-group" style={{ marginTop: '16px' }}>
                <label>Date of Verbal Consent</label>
                <AppDateInput className="pif-input" value={ccmVerbalConsentDate} onChange={setCcmVerbalConsentDate} />
              </div>
            </div>
          ) : (
            <div className="ccm-written-body">
              <div className="ccm-info-banner">
                <AlertCircle size={16} style={{ color: '#d97706', flexShrink: 0 }} />
                <span>Capture the patient signature below by drawing on the pad or uploading a signature image (JPG or PNG).</span>
              </div>
              {renderPatientSignatureField()}
            </div>
          )}
        </div>

        {/* ── Section 3: Billing Restrictions (create / first CCM enroll only) ── */}
        {(!patientToEdit || !originalEnrolledPrograms.includes('ccm')) && (
          <div className="ccm-section-block">
            <div className="ccm-section-header">
              <AlertCircle size={15} style={{ color: '#f59e0b' }} />
              <span className="ccm-section-heading">Billing Restrictions</span>
            </div>
            <label className={`ccm-restriction-row ${ccmRestrictionsConfirmed ? 'confirmed' : ''} ${errors.ccmRestrictions ? 'is-invalid' : ''}`}>
              <input
                type="checkbox"
                checked={ccmRestrictionsConfirmed}
                onChange={() => setCcmRestrictionsConfirmed(!ccmRestrictionsConfirmed)}
                className="ccm-check-input"
              />
              <span className={`ccm-check-custom ${ccmRestrictionsConfirmed ? 'checked' : ''}`}>
                {ccmRestrictionsConfirmed && <Check size={11} strokeWidth={3} />}
              </span>
              <span className="ccm-check-label">
                I confirm that this patient has <strong>no active enrollment</strong> in Transitional Care Management (TCM), ESRD services, PACE, or Hospice care for the current billing month.
              </span>
            </label>
            <FormFieldError message={errors.ccmRestrictions} />
          </div>
        )}
        <FormFieldError message={errors.chronicConditions} />
      </div>
    );
  }

  function renderPCMStep() {
    return (
      <div className="pif-ccm-combined-layout">
        <div className="pif-section">
          <PatientChronicConditionsSection
            conditions={selectedChronicConditions}
            onChange={(nextConditions) => {
              const capped = Array.isArray(nextConditions) ? nextConditions.slice(0, 1) : [];
              setSelectedChronicConditions(capped);
              syncCareManagementFromConditions(capped);
            }}
            minRequired={1}
            maxAllowed={1}
            hasError={Boolean(errors.chronicConditions)}
            errorMessage={errors.chronicConditions}
            title="Principal Condition (1 required)"
            description="Select the single high-risk chronic condition expected to last at least 3 months that places the patient at significant risk of hospitalization, exacerbation, functional decline, or death."
          />
        </div>

        <div className="pif-ccm-divider"></div>

        <div className="ccm-section-block">
          <div className="ccm-section-header">
            <FileText size={15} className="ccm-section-icon" />
            <span className="ccm-section-heading">Patient Consent</span>
          </div>

          <div className="ccm-consent-tabs">
            <button
              type="button"
              className={`ccm-consent-tab ${ccmConsentType === 'verbal' ? 'active' : ''}`}
              onClick={() => {
                setCcmConsentType('verbal');
                setErrors((prev) => {
                  if (!prev.ccmConsentSignature) return prev;
                  const next = { ...prev };
                  delete next.ccmConsentSignature;
                  return next;
                });
              }}
            >Verbal Consent</button>
            <button
              type="button"
              className={`ccm-consent-tab ${ccmConsentType === 'written' ? 'active' : ''}`}
              onClick={() => {
                setCcmConsentType('written');
              }}
            >Written Consent</button>
          </div>

          {ccmConsentType === 'verbal' ? (
            <div className="ccm-verbal-body">
              <div className="ccm-checklist">
                {[
                  'Did you explain PCM services, including that they focus on one high-risk chronic condition expected to last at least 3 months?',
                  'Did you inform the patient that PCM cannot be billed for less than 30 minutes per calendar month?',
                  'Did you inform the patient of their right to stop PCM services at any time, and that another initial visit is required after 1 year to continue?'
                ].map((q, i) => (
                  <label key={i} className={`ccm-check-row ${ccmVerbalAnswers[i] ? 'checked' : ''}`} style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      className="ccm-check-input"
                      checked={ccmVerbalAnswers[i]}
                      onChange={() => {
                        const newAnswers = [...ccmVerbalAnswers];
                        newAnswers[i] = !newAnswers[i];
                        setCcmVerbalAnswers(newAnswers);
                      }}
                    />
                    <span className={`ccm-check-custom ${ccmVerbalAnswers[i] ? 'checked' : ''}`}>
                      {ccmVerbalAnswers[i] && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className="ccm-check-label">{q}</span>
                  </label>
                ))}
              </div>
              <div className="pif-form-group" style={{ marginTop: '16px' }}>
                <label>Date of Verbal Consent</label>
                <AppDateInput className="pif-input" value={ccmVerbalConsentDate} onChange={setCcmVerbalConsentDate} />
              </div>
            </div>
          ) : (
            <div className="ccm-written-body">
              <div className="ccm-info-banner">
                <AlertCircle size={16} style={{ color: '#d97706', flexShrink: 0 }} />
                <span>Capture the patient signature below by drawing on the pad or uploading a signature image (JPG or PNG).</span>
              </div>
              {renderPatientSignatureField()}
            </div>
          )}
        </div>

        {(!patientToEdit || !originalEnrolledPrograms.includes('pcm')) && (
          <div className="ccm-section-block">
            <div className="ccm-section-header">
              <AlertCircle size={15} style={{ color: '#f59e0b' }} />
              <span className="ccm-section-heading">Billing Restrictions</span>
            </div>
            <label className={`ccm-restriction-row ${ccmRestrictionsConfirmed ? 'confirmed' : ''} ${errors.ccmRestrictions ? 'is-invalid' : ''}`}>
              <input
                type="checkbox"
                checked={ccmRestrictionsConfirmed}
                onChange={() => setCcmRestrictionsConfirmed(!ccmRestrictionsConfirmed)}
                className="ccm-check-input"
              />
              <span className={`ccm-check-custom ${ccmRestrictionsConfirmed ? 'checked' : ''}`}>
                {ccmRestrictionsConfirmed && <Check size={11} strokeWidth={3} />}
              </span>
              <span className="ccm-check-label">
                I confirm this patient is not also enrolled in CCM for the same month, and has <strong>no active enrollment</strong> in Transitional Care Management (TCM), ESRD services, PACE, or Hospice care.
              </span>
            </label>
            <FormFieldError message={errors.ccmRestrictions} />
          </div>
        )}
        <FormFieldError message={errors.chronicConditions} />
      </div>
    );
  }

  function renderPlaceholderStep(programName) {
    return (
      <div className="pif-section" style={{ textAlign: 'center', padding: '40px' }}>
        <h3 className="pif-section-title">{programName} Configuration</h3>
        <p style={{ color: '#64748b' }}>Configure the monitoring parameters for {programName} care services.</p>
        <div className="pif-form-group" style={{ maxWidth: '300px', margin: '20px auto' }}>
          <label>Start Date</label>
          <AppDateInput className="pif-input" value={placeholderProgramStartDate} onChange={setPlaceholderProgramStartDate} />
        </div>
      </div>
    );
  }
};